/**
 * German user-facing strings for the frontend. Labels, buttons, validation
 * messages — everything the practitioner reads lives here, never inlined in a
 * component. This is not i18n; it keeps the English-code / German-UI split
 * enforceable.
 */
import type { LemniscusIssue, LemniscusIssueCode } from '@praxi/shared'

export const strings = {
  app: {
    title: 'Praxisverwaltung',
    /** The sidebar header's brand line — short on purpose, the practice name
     *  underneath it already carries the rest. */
    shortTitle: 'Praxi',
  },
  nav: {
    dashboard: 'Übersicht',
    contacts: 'Kontakte',
    appointments: 'Kalender',
    activities: 'Vorgänge',
    payments: 'Zahlungen',
    services: 'Leistungen',
    settings: 'Einstellungen',
    signOut: 'Abmelden',
    collapse: 'Navigation einklappen',
    expand: 'Navigation ausklappen',
  },
  account: {
    settings: 'Einstellungen',
    settingsDescription: 'Gilt nur für dieses Konto, nicht für die ganze Praxis.',
  },
  preferences: {
    theme: {
      label: 'Farbschema',
      options: {
        slate: 'Schiefer',
        blue: 'Blau',
        sage: 'Salbei',
        rose: 'Rosé',
        night: 'Nacht',
      },
    },
    startPage: {
      label: 'Startseite nach dem Anmelden',
      options: {
        overview: 'Übersicht',
        contacts: 'Kontakte',
        calendar: 'Kalender',
        activities: 'Vorgänge',
      },
      // The effect is invisible until the next sign-in, unlike the theme —
      // without this the value just quietly changed.
      saved: 'Startseite gespeichert. Wirkt beim nächsten Anmelden.',
    },
  },
  login: {
    description: 'Bitte melden Sie sich mit Ihren Zugangsdaten an.',
    email: 'E-Mail-Adresse',
    password: 'Passwort',
    submit: 'Anmelden',
    submitting: 'Anmeldung läuft …',
    failed: 'E-Mail-Adresse oder Passwort ist falsch.',
    /** The login rate limit (S-B). One message for the whole window rather
     *  than a countdown: a remaining time is a number the screen would have to
     *  keep true, and it tells whoever is guessing exactly when to come back. */
    tooManyAttempts: 'Zu viele Anmeldeversuche. Bitte warten Sie einen Moment.',
  },
  /** The hint under each entry of the section column of `/settings`.
   *  Deliberately not the same string as the panel's own title-bar hint: the
   *  design writes two texts, a short one here and a longer one on the card,
   *  and D4 reused one for both — which is why three entries wrapped to three
   *  lines (K4). */
  settingsNav: {
    practiceHint: 'Stammdaten, Anschrift, Bank',
    invoicingHint: 'Nummernkreise, Texte, Vorlage',
    contactsHint: 'Nummer, Rollen, Auswahllisten',
    noteTypesHint: 'Wonach Notizen abgelegt werden',
    activityTypesHint: 'Farbe, Dauer, Vorbelegung',
    textTemplatesHint: 'Einleitung und Schluss',
    mailHint: 'Konto und Vorlagen',
    googleHint: 'Projektion der Termine',
    dataImportHint: 'Umzug aus Lemniscus',
  },
  settings: {
    pageTitle: 'Einstellungen',
    pageDescription: 'Was für die ganze Praxis gilt.',
    sectionPractice: 'Praxis',
    sectionAddress: 'Anschrift',
    sectionContact: 'Kontakt',
    sectionBanking: 'Bankverbindung',
    sectionTaxes: 'Steuern',
    sectionInvoicingPreset: 'Rechnungsstellung',
    /** One card, six sections. The six explanations that used to stand in the
     *  title column beside them are gone (B1, L1): "Der Name steht auf jeder
     *  Rechnung", "Für Rückfragen der Empfänger" and the rest said what the
     *  heading above them already said, six times down one column. The
     *  headings stay. The contact record keeps its own section hints — there
     *  they carry rules a reader could not guess. */
    cardTitle: 'Praxisstammdaten',
    vatId: 'Umsatzsteuer-ID',
    sectionInvoicing: 'Rechnungsstellung',
    sectionOpeningHours: 'Öffnungszeiten',
    practiceName: 'Praxisname',
    street: 'Straße und Hausnummer',
    postalCode: 'PLZ',
    city: 'Ort',
    country: 'Land',
    phone: 'Telefon',
    email: 'E-Mail-Adresse',
    website: 'Website',
    taxNumber: 'Steuernummer',
    bankName: 'Bank',
    iban: 'IBAN',
    bic: 'BIC',
    defaultPaymentTermDays: 'Zahlungsziel in Tagen',
    save: 'Speichern',
    saving: 'Wird gespeichert …',
    saved: 'Praxisstammdaten gespeichert.',
    saveFailed: 'Die Praxisstammdaten konnten nicht gespeichert werden.',
  },
  /** "Nächste Kontaktnummer", the first card of the settings section
   *  "Kontakte" (#56). A contact number is a running whole number: no prefix,
   *  no number of digits. */
  contactNumber: {
    sectionTitle: 'Kontakte',
    title: 'Kontaktnummer',
    hint: 'wird fortlaufend vergeben',
    nextValue: 'Nächste Kontaktnummer',
    /** While the range does not exist: nothing is stored, so no figure is
     *  shown — only what will happen. */
    notSet: 'Noch nicht festgelegt. Der erste Kontakt erhält die Nummer 1.',
    footer:
      'Jeder neue Kontakt erhält diese Nummer, danach zählt sie um eins weiter. Eine Änderung ' +
      'ist nur nötig, um eine bestehende Nummerierung fortzusetzen; die Nummer muss dann über ' +
      'allen bereits vergebenen liegen.',
    invalid: 'Eine ganze Zahl von 1 bis 2147483647.',
    saved: 'Nächste Kontaktnummer gespeichert.',
  },
  /** The three value lists behind a contact's own fields (D-R3). They stand
   *  in the settings section "Kontakte" since #56. */
  valueList: {
    salutationsTitle: 'Anreden',
    salutationsHint: 'stehen vor dem Namen in der Anschrift',
    salutationsFooter:
      'Eine Anrede gilt für Personen und Organisationen gleichermaßen — „Firma Mustermann ' +
      'GmbH" ist die übliche erste Zeile einer deutschen Anschrift.',
    createSalutation: 'Neue Anrede',
    salutationsEmptyCard: 'Noch keine Anrede angelegt.',

    gendersTitle: 'Geschlechter',
    gendersHint: 'stehen nur bei Personen',
    createGender: 'Neues Geschlecht',
    gendersEmptyCard: 'Noch kein Geschlecht angelegt.',

    countriesTitle: 'Länder',
    countriesHint: 'stehen in der Anschrift zur Auswahl',
    countrySearch: 'Land hinzufügen',
    /** A search, not a dropdown: the ISO list has some 250 entries. */
    countrySearchPlaceholder: 'Name oder Kürzel eingeben',
    countryNoMatch: 'Kein Land gefunden.',
    countriesEmptyCard: 'Noch kein Land angelegt.',

    deleteTitle: 'Eintrag löschen?',
    deleteCountryTitle: 'Land aus der Liste nehmen?',
    deleteBody: 'Das lässt sich nicht rückgängig machen.',
  },
  contactType: {
    tabRoles: 'Rollen',
    /** The card manages the *types* of relations, and says so. */
    relationCardTitle: 'Beziehungsarten',
    rolesHint: 'bestimmen, in welchen Listen ein Kontakt auftaucht',
    relationsHint: 'verbinden zwei Kontakte und erscheinen in beiden Akten',
    /** The direction in words, in the design's wording (K4). */
    symmetricSummary: 'Gilt in beide Richtungen gleich',
    exclusiveSummary: 'Höchstens einmal pro Kontakt',
    counterpartSummary: (inverse: string) => `Gegenstück: ${inverse}`,
    /** Roles carry no logic since migration 0035, so nothing about them is
     *  protected — what the footer says instead is the one rule that is left. */
    rolesFooter:
      'Eine Rolle ist eine reine Kennzeichnung. Sie lässt sich jederzeit umbenennen und ' +
      'löschen, solange kein Kontakt sie trägt.',
    relationsFooter:
      'Der erste Kontakt ist der, in dessen Akte der Sachverhalt eine Eigenschaft dieses ' +
      'Kontakts ist — das Kind hat einen Sorgeberechtigten. Der zweite Kontakt ist das Gegenüber.',

    /** Only ever a LABEL now (B1d): no screen offers a Kürzel to type, and it
     *  is shown only on a system entry, where the software greps for it and
     *  the read-only form says so. `codeHint` and `codeFixed` went with the
     *  field — an own type's code is derived from its label and nobody sees
     *  it. */
    code: 'Kürzel',
    direction: 'Richtung',
    /** Above the read-only form of a system entry (B1). Names the consequence
     *  rather than the rule: what a rename would break is that the record and
     *  the invoice would stop agreeing. */
    systemReadOnlyHint:
      'Dieser Eintrag gehört fest zum System — die Software richtet sich nach seinem Kürzel. ' +
      'Bezeichnungen, Richtung und Exklusivität sind deshalb festgelegt; nur „Aktiv" lässt ' +
      'sich umstellen.',
    /** Beside the one control a system entry still has. Says what switching it
     *  off does and, just as importantly, what it does not do. */
    systemActiveHint:
      'Inaktive Arten stehen nicht mehr zur Auswahl. Bestehende Beziehungen bleiben, wie sie ' +
      'sind.',
    label: 'Bezeichnung',
    /** The label is what a role is recognised by now that there is no code —
     *  hence unique, and hence worth saying at the field. */
    roleLabelHint: 'Jede Rolle braucht eine eigene Bezeichnung.',
    /** The read view of a flag. Repeating the flag's own name as its value —
     *  "Als Reiter in der Kontaktliste: Als Reiter in der Kontaktliste" — was
     *  invisible while a second field stood beside it and glaring once the
     *  code went. */
    flagYes: 'Ja',
    labelForward: 'Bezeichnung in der Akte des ersten Kontakts',
    labelInverse: 'Bezeichnung in der Akte des zweiten Kontakts',
    /** The direction convention, in one sentence, where it is decided. */
    directionHint:
      'Der erste Kontakt ist der, in dessen Akte der Sachverhalt eine Eigenschaft dieses ' +
      'Kontakts ist — das Kind hat einen Sorgeberechtigten, der Patient hat einen ' +
      'Rechnungsempfänger. Der zweite Kontakt ist das Gegenüber.',
    /** The radio pair replacing a single "einseitig" checkbox — nothing here
     *  is inverted relative to `isSymmetric`, and nothing has to be
     *  remembered (D4, correcting the prototype's own inverted checkbox). */
    directionMutualLabel: 'Gegenseitig — beide Seiten heißen gleich',
    directionMutualExample: 'Beispiel: Ehepartner von / Ehepartner von',
    directionDirectedLabel: 'Gerichtet — jede Seite hat eine eigene Bezeichnung',
    directionDirectedExample: 'Beispiel: Elternteil von / Kind von',
    exclusive: 'Höchstens einmal pro Kontakt',
    exclusiveHint:
      'Zum Beispiel der Rechnungsempfänger: ein Kontakt hat höchstens einen — umgekehrt darf ' +
      'ein Rechnungsempfänger für mehrere Kontakte zuständig sein.',
    showAsTab: 'Als Reiter in der Kontaktliste',
    active: 'Aktiv',
    systemBadge: 'System',
    exclusiveBadge: 'Nur einmal',
    symmetricBadge: 'Beidseitig',
    /** Renaming was allowed until B1 and is not anymore — see
     *  `systemReadOnlyHint`. */
    systemHint:
      'Auf diesem Eintrag baut die Software auf. Er lässt sich weder umbenennen noch löschen, ' +
      'nur auf inaktiv setzen.',

    createRole: 'Neue Rolle',
    createRelation: 'Neue Beziehungsart',

    emptyRoles: 'Keine Rollen vorhanden.',
    emptyRelations: 'Keine Beziehungsarten vorhanden.',
    saved: 'Eintrag gespeichert.',
    deleted: 'Eintrag gelöscht.',
    saveFailed: 'Der Eintrag konnte nicht gespeichert werden.',
    deleteTitle: 'Eintrag löschen?',
    deleteBody:
      'Der Eintrag verschwindet aus der Auswahl. Solange er noch verwendet wird, lässt er sich ' +
      'nicht löschen — setzen Sie ihn dann auf inaktiv.',
  },
  activityType: {
    title: 'Vorgangsarten',
    hint: 'Farbe im Kalender und Vorbelegung beim Anlegen',
    /** Deliberately no footer under this card (B1). It said what `activeHint`
     *  says beside the checkbox that does it — and a sentence standing twice on
     *  one screen is not read twice, it is read once at best. The hint stays,
     *  because it is at the control; the footer went, because it explained a
     *  mechanism nobody was invoking. Same call as F2 at the Notizarten. */
    /** Not `—`: the point is that this kind of activity has no usual length,
     *  which is a statement, where a dash would read as a missing value. */
    noUsualDuration: 'ohne übliche Dauer',

    create: 'Neue Vorgangsart',
    empty: 'Noch keine Vorgangsarten.',

    /** No `code`/`codeHint` here since migration 0041 — see the catalogue's
     *  schema. The label is what a type is recognised by, hence unique. */
    label: 'Bezeichnung',
    labelHint: 'Jede Vorgangsart braucht eine eigene Bezeichnung.',
    color: 'Farbe',
    colorHint: 'Damit wird der Termin im Kalender hinterlegt.',
    defaultDuration: 'Dauer in Minuten',
    defaultDurationHint: 'Leer lassen, wenn es keine übliche Dauer gibt.',
    preset: 'Leistungen',
    presetLongHint:
      'Wird beim Anlegen eines Vorgangs als Position eingesetzt. Eine Gruppe wird dabei sofort ' +
      'in einzelne Positionen aufgelöst und erscheint selbst nie in dieser Liste. Spätere ' +
      'Änderungen hier wirken sich auf nichts Bestehendes aus.',
    presetEmpty: 'Keine Leistung vorbelegt.',
    presetAdd: 'Leistung hinzufügen',
    presetService: 'Leistung',
    presetGroup: 'Leistungsgruppe',
    presetQuantity: 'Menge',
    /** "Entfernen" and not "Löschen": the service stays in the catalogue, it
     *  only leaves this preset (B1, convention A3). */
    presetRemove: 'Aus der Vorbelegung nehmen',
    isDefault: 'Standard für neue Vorgänge',
    isDefaultHint: 'Genau eine Art kann das sein. Die bisherige verliert die Markierung.',
    defaultBadge: 'Standard',
    active: 'Aktiv',
    activeHint: 'Inaktive Arten erscheinen in keiner Auswahlliste, bleiben aber an Vorgängen.',

    saved: 'Vorgangsart gespeichert.',
    deleted: 'Vorgangsart gelöscht.',
    saveFailed: 'Die Vorgangsart konnte nicht gespeichert werden.',
    deleteTitle: 'Vorgangsart löschen?',
    deleteBody:
      'Die Art verschwindet aus der Auswahl. Solange sie noch von Vorgängen verwendet wird, ' +
      'lässt sie sich nicht löschen — setzen Sie sie dann auf inaktiv.',
  },
  validation: {
    required: 'Dieses Feld ist erforderlich.',
    email: 'Bitte eine gültige E-Mail-Adresse eingeben.',
    iban: 'Diese IBAN ist ungültig.',
    paymentTerm: 'Bitte eine Zahl zwischen 0 und 365 eingeben.',
    tooLong: 'Diese Eingabe ist zu lang.',
    amount: 'Bitte einen Betrag eingeben, zum Beispiel 90,00.',
    duration: 'Bitte eine Dauer in Minuten eingeben oder das Feld leer lassen.',
  },
  contact: {
    title: 'Kontakte',
    description: 'Personen und Organisationen der Praxis.',
    create: 'Neuer Kontakt',
    createTitle: 'Kontakt anlegen',
    search: 'Suchen',
    searchPlaceholder: 'Name, Firma oder Kontaktnummer',
    /** The filter bar shows no labels (K6), so these two are the accessible
     *  names of the search field and the role menu and nothing else. */
    moreRolesMenu: 'Weitere Rollen',
    showArchived: 'Archivierte anzeigen',
    empty: 'Keine Kontakte vorhanden.',
    emptyFiltered: 'Kein Kontakt passt zu dieser Suche.',
    /** The page size travels with it: the line only appears when the list is
     *  truncated, and then "why 50 of 214" is the next question. */
    countOf: (shown: number, total: number) => `${shown} von ${total} angezeigt`,
    /** The list runs on as it is scrolled, so this says how far along it is —
     *  not how much was withheld, which is what "50 von 214" implied. */
    countLoaded: (shown: number, total: number) =>
      shown >= total ? `${total} Kontakte` : `${shown} von ${total} geladen`,
    archivedBadge: 'Archiviert',

    kindLabel: 'Art',
    kind: {
      person: 'Person',
      organization: 'Organisation',
    },

    roleLabel: 'Rollen',
    /** The labels themselves come from `contact_role_type` — the practitioner
     *  maintains them in the settings, so there is nothing to translate here.
     *
     *  With none set up at all the section would otherwise be blank and read
     *  as broken, so it says in words that this is a legitimate state. */
    roleTypesEmpty: 'Es sind keine Rollen angelegt.',
    roleTypesEmptyHint:
      'Ein Kontakt braucht keine — Rollen sind eine Kennzeichnung, die bestimmt, in ' +
      'welchen Listen er auftaucht. Angelegt werden sie in den Einstellungen unter ' +
      '„Kontakte“.',
    moreRoles: 'Weitere',
    searchAll: 'Die Suche geht über alle Kontakte — unabhängig von der Rolle.',
    allRolesTab: 'Alle',
    noRoles: 'Keine Rolle zugewiesen',

    writeMail: 'E-Mail schreiben',
    callNumber: 'Nummer anrufen',

    relations: 'Verknüpfte Kontakte',
    relationsEmpty: 'Keine verknüpften Kontakte hinterlegt.',
    relationAdd: 'Beziehung hinzufügen',
    relationKind: 'Art der Beziehung',
    relationKindChoose: 'Bitte wählen',
    relationOther: 'Kontakt',
    relationActions: 'Aktionen',
    relationSave: 'Speichern',
    relationEdit: 'Beziehung bearbeiten',
    relationAdded: 'Beziehung hinzugefügt.',
    relationSaved: 'Beziehung gespeichert.',
    relationRemoved: 'Beziehung entfernt.',
    relationRemove: 'Entfernen',
    relationTaken: 'bereits gesetzt',
    relationRemoveTitle: 'Beziehung entfernen?',
    relationRemoveBody:
      'Die Beziehung verschwindet aus beiden Akten. Die Kontakte selbst bleiben unverändert.',
    relationFailed: 'Die Beziehung konnte nicht gespeichert werden.',
    relationNoTypes:
      'Es sind keine Beziehungsarten hinterlegt. Sie legen sie in den Einstellungen an.',

    /** The fourteen the list offers (L4). Broad on offer, narrow by default —
     *  what a practice wants in its card index is its own business. */
    columns: {
      number: 'Nr.',
      name: 'Name',
      roles: 'Rollen',
      street: 'Straße',
      houseNumber: 'Hausnummer',
      postalCode: 'PLZ',
      city: 'Ort',
      country: 'Land',
      email: 'E-Mail',
      phoneMobile: 'Mobil',
      phoneLandline: 'Festnetz',
      dateOfBirth: 'Geburtsdatum',
      kind: 'Art',
      archived: 'Archiviert',
    },

    sectionName: 'Name',
    sectionNameHint: 'Art ist strukturell und nach dem Anlegen nicht mehr änderbar.',
    sectionRoles: 'Rollen',
    /** One sentence for both screens: since K6 the roles are edited in the
     *  master data on an existing contact too, so "later in the header" —
     *  which the create prototype still says — is no longer true anywhere. */
    sectionRolesHint: 'Bestimmen, in welchen Listen der Kontakt auftaucht.',
    sectionPerson: 'Person',
    sectionPersonHint: 'Geburtsdatum und Geschlecht stehen auf Rezept und Abrechnung.',
    sectionTax: 'Steuer',
    sectionTaxHint: 'Erscheint auf Rechnungen an diesen Kontakt.',
    /** The read mode of the roles section says only what is missing, and only
     *  when everything is (L5). `rolesUnassigned`, which listed the catalogue
     *  minus this contact's roles, went with the line it filled.
     *  The second half is the point: "no role" reads as a shortcoming and is
     *  none, but it does have a visible consequence. */
    rolesNone: 'Keine Rolle zugeordnet — der Kontakt taucht dadurch nur unter „Alle" auf.',
    masterReadHint: 'Stammdaten werden erst nach „Bearbeiten" änderbar.',
    unsavedChanges: 'Ungespeicherte Änderungen',
    rolesSaveFailed:
      'Die Stammdaten sind gespeichert, die Rollen nicht. Bitte noch einmal versuchen.',
    createHint: 'Die Kontaktnummer wird beim Speichern vergeben.',
    sectionAddress: 'Anschrift',
    sectionAddressHint: 'Hausnummer separat, für Rechnung und Anzeige wieder zusammengesetzt.',
    sectionContact: 'Kontakt',
    sectionContactHint:
      'Mobil und Festnetz getrennt — die Art entscheidet, ob man anruft oder schreibt.',
    sectionInternal: 'Intern',
    salutation: 'Anrede',
    salutationNone: 'Keine Anrede',
    academicTitle: 'Titel',
    firstName: 'Vorname',
    lastName: 'Nachname',
    dateOfBirth: 'Geburtsdatum',
    birthPlace: 'Geburtsort',
    gender: 'Geschlecht',
    genderNone: 'Keine Angabe',
    countryNone: 'Kein Land',
    /**
     * The empty state of the three value lists (D-R3). Each says the same
     * three things: that the list is empty, that this is allowed, and where it
     * is filled — the last of which is one string for all three.
     *
     * Not a placeholder waiting to be filled in: an empty dropdown looks
     * broken and explains nothing, which is why the field becomes a sentence
     * instead. Same shape as the roles section since 0035.
     */
    salutationsEmpty: 'Es sind keine Anreden angelegt.',
    salutationsEmptyHint: 'Ein Kontakt braucht keine.',
    gendersEmpty: 'Es sind keine Geschlechter angelegt.',
    gendersEmptyHint: 'Ein Kontakt braucht keines.',
    countriesEmpty: 'Es sind keine Länder angelegt.',
    countriesEmptyHint:
      'Eine Anschrift braucht kein Land — bei Deutschland steht es ohnehin nie auf der Rechnung.',
    valueListEmptySettings: 'Angelegt werden sie in den Einstellungen unter „Kontakte“.',
    companyName: 'Firmenname',
    contactPerson: 'Ansprechpartner',
    vatId: 'USt-IdNr.',
    street: 'Straße',
    houseNumber: 'Hausnummer',
    postalCode: 'PLZ',
    city: 'Ort',
    country: 'Land',
    email: 'E-Mail-Adresse',
    phoneMobile: 'Mobil',
    phoneLandline: 'Festnetz',
    internalNote: 'Interne Notiz',
    internalNoteHint: 'Nur intern sichtbar, erscheint auf keinem Dokument.',
    internalNotePlaceholder: 'Nur intern sichtbar',
    diagnosis: 'Diagnose',
    diagnosisHint: 'Vertraulich — erscheint nur hier, im Rechnungsentwurf und auf der Rechnung.',
    contactNumber: 'Kontaktnummer',
    /** In the record header, where the word would only pad the line. */
    numberShort: 'Nr.',

    save: 'Speichern',
    saving: 'Wird gespeichert …',
    created: 'Kontakt angelegt.',
    saved: 'Kontakt gespeichert.',
    saveFailed: 'Der Kontakt konnte nicht gespeichert werden.',

    archive: 'Archivieren',
    unarchive: 'Wiederherstellen',
    archived: 'Kontakt archiviert.',
    unarchived: 'Kontakt wiederhergestellt.',
    archiveTitle: 'Kontakt archivieren?',
    archiveBody:
      'Der Kontakt verschwindet aus der Liste, bleibt aber erhalten und kann jederzeit ' +
      'wiederhergestellt werden. Gelöscht wird nichts.',
    cancel: 'Abbrechen',

    ageYears: (years: number) => `${years} Jahre`,
    editRoles: 'Rollen bearbeiten',

    overviewThread: 'Termine und Vorgänge',
    nextAppointment: 'Nächster Termin',
    noNextAppointment: 'Kein Termin geplant.',
    lastActivity: 'Letzter Vorgang',
    document: 'Dokumentieren',
    recentActivities: 'Letzte Vorgänge',
    documented: 'Dokumentiert',
    notDocumented: 'Nicht dokumentiert',
    noContactData: 'Keine Kontaktdaten hinterlegt.',
    billable: 'Abrechenbar',
    noBillable: 'Nichts offen.',
    billableCount: (count: number) =>
      count === 1 ? '1 offene Position' : `${count} offene Positionen`,
    openDraft: 'Zum Rechnungsentwurf',
    /** On the card the number is set large on its own line, so this counts
     *  without repeating the word "Rechnung" that heads the card (K7). */
    invoicesFinalized: (count: number) => `${count} festgeschrieben`,
    invoicesOpenAmount: 'offen',
    invoicesOverdueAmount: 'davon überfällig',
    invoicesBilledInYear: (year: number) => `abgerechnet ${year}`,
    invoicesSettled: 'Nichts offen.',
    invoicesOverdue: (count: number) => (count === 1 ? '1 überfällig' : `${count} überfällig`),
    toInvoices: 'Zu den Rechnungen',
    guardianMissing:
      'Dieser Kontakt ist noch nicht volljährig, und es ist niemand als sorgeberechtigt ' +
      'hinterlegt. Sie können das unter „Verknüpfte Kontakte" ergänzen.',

    tabs: {
      overview: 'Übersicht',
      master: 'Stammdaten',
      notes: 'Notizen',
      activities: 'Vorgänge',
      appointments: 'Termine',
      invoices: 'Rechnungen',
    },
  },
  service: {
    title: 'Leistungen',
    description: 'Der Leistungskatalog. Vorlagen für Vorgänge und Rechnungen.',
    /** rule 5 in `.claude/rules/billing.md` — worth saying on screen, because it is the opposite
     *  of what most catalogues do. */
    templateHint:
      'Der Katalog ist eine Vorlage. Beim Anlegen eines Vorgangs werden Bezeichnung, Ziffer, ' +
      'Preis und Dauer kopiert — spätere Änderungen hier wirken sich auf nichts Bestehendes aus.',

    tabServices: 'Leistungen',
    tabGroups: 'Leistungsgruppen',

    create: 'Neue Leistung',
    createTitle: 'Leistung anlegen',
    createHint: 'wird in den Katalog aufgenommen',
    empty: 'Noch keine Leistungen im Katalog.',

    shortCode: 'Kürzel',
    shortCodeHint: 'Optional, für die Schnellauswahl. Muss eindeutig sein.',
    serviceDescription: 'Bezeichnung',
    feeCode: 'Ziffer (GebüH)',
    /** The column heading only. The form and the detail field keep the long
     *  form — that is how the design labels each of the three (K5). */
    feeCodeColumn: 'Ziffer',
    price: 'Preis',
    priceHint: 'In Euro, zum Beispiel 90,00',
    duration: 'Dauer',
    durationMinutes: 'Minuten',
    durationEmpty: 'ohne Dauer',
    active: 'Aktiv',
    activeHint: 'Inaktive Leistungen erscheinen in keiner Auswahlliste.',
    /** The inline detail's own hint — a fuller sentence than `activeHint`,
     *  which is the checkbox's caption in the form. */
    detailHintActive: 'Erscheint in allen Auswahllisten.',
    detailHintInactive: 'Erscheint in keiner Auswahlliste, bleibt aber an bestehenden Vorgängen.',
    deleteTitle: 'Leistung löschen?',
    deleteBody:
      'Die Leistung verschwindet aus dem Katalog. Vorgänge und Rechnungen, die sie bereits ' +
      'übernommen haben, bleiben unverändert — dort ist die Leistung kopiert.',
    deleted: 'Leistung gelöscht.',
    deleteFailed: 'Die Leistung konnte nicht gelöscht werden.',

    groupCreate: 'Neue Gruppe',
    groupCreateTitle: 'Leistungsgruppe anlegen',
    groupCreateHint: 'wird zur Auswahl beim Anlegen eines Vorgangs',
    groupEmpty: 'Noch keine Leistungsgruppen.',
    groupColumnLabel: 'Gruppe',
    groupColumnContains: 'Enthalten',
    groupColumnCount: 'Anzahl',
    groupName: 'Name',
    groupItems: 'Enthaltene Leistungen',
    groupItemsEmpty: 'Noch keine Leistung hinzugefügt.',
    groupChooseService: 'Leistung wählen',
    groupQuantity: 'Menge',
    groupSum: 'Summe',
    groupRemove: 'Entfernen',
    groupCount: (count: number) => (count === 1 ? '1 Leistung' : `${count} Leistungen`),
    /** A group may hold a service that was deactivated afterwards. */
    groupInactiveService: 'Diese Leistung ist inaktiv.',
    groupHint:
      'Eine Gruppe ist nur eine Auswahlhilfe. Beim Anlegen eines Vorgangs wird sie sofort in ' +
      'einzelne Positionen aufgelöst; gespeichert wird kein Verweis auf die Gruppe.',
    groupDeleteTitle: 'Leistungsgruppe löschen?',
    groupDeleteBody:
      'Die Gruppe verschwindet aus der Auswahl. Die enthaltenen Leistungen bleiben im Katalog.',
    groupDeleted: 'Leistungsgruppe gelöscht.',
    groupDeleteFailed: 'Die Leistungsgruppe konnte nicht gelöscht werden.',

    save: 'Speichern',
    saving: 'Wird gespeichert …',
    cancel: 'Abbrechen',
    created: 'Leistung angelegt.',
    saved: 'Leistung gespeichert.',
    groupCreated: 'Leistungsgruppe angelegt.',
    groupSaved: 'Leistungsgruppe gespeichert.',
    saveFailed: 'Der Eintrag konnte nicht gespeichert werden.',
  },
  activity: {
    title: 'Vorgänge',
    description: 'Was wann für wen erbracht wurde.',
    create: 'Neuer Vorgang',
    createTitle: 'Neuer Vorgang',
    /** Beside the heading of the inline form, in the idiom L6b settled on for
     *  a record that does not exist yet. */
    createPending: 'wird noch angelegt',
    createHint: 'Was erbracht wurde, und ob ein Termin dazugehört.',
    editTitle: 'Vorgang bearbeiten',
    /** The calendar's dialog in read mode — it is not an editor until
     *  "Bearbeiten" is pressed, and the heading must not say otherwise. */
    detailTitle: 'Vorgang',
    detailHint: 'Zum Ändern auf „Bearbeiten“.',
    empty: 'Noch keine Vorgänge.',

    type: 'Art',
    statusLabel: 'Status',
    statuses: {
      planned: 'Geplant',
      rendered: 'Stattgefunden',
      no_show: 'Nicht erschienen',
    },
    allTypes: 'Alle Arten',
    rangeFrom: 'Von',
    rangeTo: 'Bis',
    /** The line above the list. Reads as one sentence rather than three
     *  figures: it is the reason the page gets opened. */
    summary: (total: number, upcoming: number, unbilled: string) =>
      `${total} ${total === 1 ? 'Vorgang' : 'Vorgänge'} · ${upcoming} kommend · ` +
      `${unbilled} noch nicht abgerechnet`,
    /** A new activity can belong anywhere — a session five years back that is
     *  being documented late lands below everything that has been loaded. The
     *  strip says where it went, so nothing reads as "not saved". */
    createdElsewhere: (date: string) => `Neu angelegt — steht in der Liste beim ${date}.`,
    /** The other reason it is not on screen: it does not match what the list
     *  is currently showing. Saying so beats a strip that points into a list
     *  the row is not in. */
    createdOutsideFilter: (date: string) =>
      `Neu angelegt zum ${date} — gehört nicht in die aktuelle Auswahl.`,
    /** Not "Kommende Termine" (L7): these are Vorgänge, and one of them may
     *  well have no Termin at all. */
    sectionUpcoming: 'Anstehend',
    sectionPast: 'Bisher',
    /** What the dialog says instead of quietly overwriting. */
    presetsUnchanged: 'Dauer und Positionen bleiben unverändert.',
    presetsApply: 'Aus der Vorgangsart übernehmen',
    presetsApplied: 'Vorbelegung übernommen.',
    contact: 'Kontakt',
    contactSearch: 'Name oder Kontaktnummer tippen',
    contactNoResults: 'Kein Kontakt gefunden. Archivierte Kontakte werden nicht vorgeschlagen.',
    contactLocked: 'fest',
    contactChange: 'Anderer Kontakt',
    contactRequired: 'Bitte zuerst einen Kontakt wählen.',
    /** The calendar's panel (D-K3). */
    newEntry: 'Neuer Eintrag',
    newEntryAt: (date: string, time: string) => `${date} · ${time}`,
    createSubmit: 'Vorgang anlegen',
    tabs: { activity: 'Vorgang mit Termin', appointment: 'Nur Termin' },
    occurredAt: 'Datum',
    occurredTime: 'Uhrzeit',
    durationMin: 'Dauer (Min.)',
    activityTitle: 'Bezeichnung',
    optional: '(optional)',
    internalNote: 'Interne Notiz',
    internalNoteHint: 'Nur intern sichtbar, erscheint auf keinem Dokument.',

    items: 'Positionen',
    itemsEmpty: 'Noch keine Position.',
    addService: 'Leistung hinzufügen',
    addGroup: 'Leistungsgruppe einfügen',
    /** The one picker that replaced those two in the calendar (D-K3). */
    addFromCatalogue: 'Aus dem Katalog hinzufügen',
    catalogueServices: 'Leistungen',
    catalogueGroups: 'Leistungsgruppen',
    groupSize: (count: number) => `${count} ${count === 1 ? 'Position' : 'Positionen'}`,
    addFree: 'Freie Position',
    addFreeShort: 'Freie Position',
    /** Between the picker and the button — one decision, two ways in. */
    addOr: 'oder',
    /** Rule 5 and rule 5 one level up, in one line under the picker — where
     *  the copying is about to happen rather than above a list of what has
     *  already been copied. */
    copyHint:
      'Aus dem Katalog wird kopiert; die Vorbelegung kommt aus der Art. ' +
      'Eine Gruppe wird sofort in einzelne Positionen aufgelöst.',
    itemDescription: 'Bezeichnung',
    itemFeeCode: 'Ziffer',
    itemQuantity: 'Menge',
    itemPrice: 'Einzelpreis',
    itemBillable: 'Abrechenbar',
    itemRemove: 'Position entfernen',
    itemMoveUp: 'Nach oben',
    itemMoveDown: 'Nach unten',
    sumBillable: 'Abrechenbar',
    sumTotal: 'Gesamt',
    sumTotalLong: 'Gesamt inkl. nicht abrechenbarer Positionen',
    notBillableBadge: 'nicht abrechenbar',
    section: 'Vorgang',
    appointmentSection: 'Termin',
    noAppointment: 'Kein Kalendertermin',
    /** "Termin Bestätigt" — the word is part of the badge, so the slot's
     *  status cannot be mistaken for the activity's own (K7). */
    appointmentBadge: (status: string) => `Termin ${status}`,
    openInCalendar: 'Im Kalender öffnen',
    /** In the detail's rail, so the length is readable without doing the
     *  subtraction. */
    durationSuffix: (minutes: number) => `${minutes} Min.`,
    /** The Notizen block of the detail (L7). Written and locked on the
     *  Notizen tab, which is where the button leads. */
    notesEmpty: 'Noch keine Notizen.',
    notesAdd: 'Notiz hinzufügen',
    close: 'Schließen',

    withAppointment: 'Termin anlegen',
    appointmentTo: 'Ende',
    durationRequired: 'Für einen Termin wird eine Dauer gebraucht.',
    appointmentStatus: 'Status',
    appointmentNote: 'Notiz zum Termin',

    save: 'Speichern',
    saving: 'Wird gespeichert …',
    cancel: 'Abbrechen',
    created: 'Vorgang angelegt.',
    saved: 'Vorgang gespeichert.',
    saveFailed: 'Der Vorgang konnte nicht gespeichert werden.',
  },
  /** "Freien Termin finden" (D9.5) — a fixture of the calendar's left rail
   *  since D-K2, where it used to be a third state of the single rail. */
  slotFinder: {
    title: 'Freien Termin finden',
    clear: 'Auswahl aufheben',
    minutes: (value: number) => `${value} Min`,
    orDuration: 'Oder nach freier Dauer',
    /** What the panel is for, before anything is chosen. */
    hintIdle: 'Vorgangsart wählen — der Kalender zeigt dann alle Zeiten, in die sie passt.',
    /** And what to do with the answer, once it is on the grid. */
    hintChoosing: 'Klick auf eine freie Zeit legt dort einen Vorgang mit Termin an.',
    empty: 'In diesem Zeitraum ist nichts frei. Blättern Sie weiter.',
    /** Why the list of activity types is short — a missing entry, not a bug. */
    typesWithoutDuration: 'Arten ohne hinterlegte Dauer stehen hier nicht.',
    toActivityTypes: 'Vorgangsarten pflegen',
    noOpeningHours:
      'Ohne Öffnungszeiten kann nicht gesucht werden — sonst würde ein Arbeitstag geraten.',
    toOpeningHours: 'Öffnungszeiten hinterlegen',
    /** No connection, no selected calendar, or the query failed — all three
     *  mean the same thing here, so they read the same. */
    privateNotChecked:
      'Private Termine wurden nicht geprüft. Die Vorschläge kennen nur die Termine der Praxis.',
    slotLabel: (from: string, to: string) => `${from}–${to} frei`,
  },
  /** The weekly opening pattern (D9.5). A day with no window is closed, and
   *  the screen says that word rather than showing empty time fields. */
  openingHours: {
    title: 'Öffnungszeiten',
    hint:
      'Grundlage für die Terminsuche. Ein Tag ohne Zeitfenster gilt als geschlossen; ' +
      'eine Mittagspause ist einfach eine Lücke zwischen zwei Fenstern.',
    weekdays: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'],
    closed: 'Geschlossen',
    from: 'Von',
    to: 'Bis',
    addWindow: 'Zeitfenster',
    removeWindow: 'Zeitfenster entfernen',
    saved: 'Öffnungszeiten gespeichert.',
  },
  appointment: {
    /** A calendar entry with no title, no contact and no type — all a bare
     *  appointment is obliged to have is a time. */
    untitled: 'Termin',
    /** The bare appointment — the "Nur Termin" tab (D-K3). */
    titlePlaceholder: 'z. B. Teambesprechung',
    optionalSuffix: '(optional)',
    createSubmit: 'Termin anlegen',
    /** Two different gestures, and the difference is not cosmetic — see
     *  `AppointmentDetail`. */
    cancelAction: 'Absagen',
    /** The block the grid draws while the panel is open (design image 13). */
    draftTitle: 'Neuer Termin',
    draftKind: (type: string) => `Vorgang · ${type}`,
    draftClash: '⚠ Überschneidet sich',
    clashHint: 'Diese Zeit überschneidet sich mit einem bestehenden Termin.',
    deleteAction: 'Löschen',
    headerSpan: (date: string, from: string, to: string) => `${date} · ${from}–${to}`,
    fromLabel: 'Von',
    toLabel: 'Bis',
    durationLabel: 'Dauer (Min)',
    noteLabel: 'Notiz',
    notePlaceholder: 'Notiz zum Termin',
    bareHint: 'Der Termin erscheint beim Kontakt, ohne einen Vorgang anzulegen.',
    title: 'Termine',
    description: 'Kalender der Praxis.',
    today: 'Heute',
    previous: 'Zurück',
    next: 'Weiter',
    /** Three views (D9). The month was dropped because at six sessions a day
     *  its cells would read "+5 weitere", and the list view is the Vorgänge
     *  page — an appointment cannot exist without its activity. */
    views: {
      day: 'Tag',
      workweek: 'Arbeitswoche',
      week: 'Woche',
      month: 'Monat',
      list: 'Liste',
    },
    /** The month cell shows three entries and counts the rest (design). */
    moreInDay: (count: number) => `+ ${count} weitere`,
    /** Both views answer with nothing on a quiet week. */
    listEmpty: 'Keine Termine in diesem Zeitraum.',
    empty: 'Keine Termine in diesem Zeitraum.',
    newHere: 'Termin anlegen',
    newAppointment: 'Neuer Termin',
    calendarWeek: (week: number) => `KW ${week}`,
    /** The right-hand rail. */
    dayOverview: 'Tagesüberblick',
    daySchedule: 'Ablauf',
    dayEmpty: 'Keine Termine an diesem Tag.',
    countAppointments: 'Termine',
    /** "belegt" and not "Stunden": the number already carries the unit
     *  ("4,5 h"), so the label may say what the hours *are* (K10). */
    countHours: 'belegt',
    countCancelled: 'Absagen',
    /** The card under the three figures: the first gap long enough for the
     *  length the finder is set to, and the way straight into it. */
    nextFree: 'Nächste freie Zeit',
    nextFreeNone: (minutes: number) => `Keine freie Zeit ab ${minutes} Min`,
    nextFreeAction: 'Termin dort anlegen',
    nextFreeSpan: (from: string, to: string, minutes: number) => `${from}–${to} · ${minutes} Min`,
    previousMonth: 'Vorheriger Monat',
    nextMonth: 'Nächster Monat',
    /** Dragging. The block springs back on refusal, so the message explains a
     *  movement the practitioner has already seen undone. */
    dragOverlap: 'Überschneidet sich',
    moved: 'Termin verschoben.',
    moveFailed: 'Der Termin konnte nicht verschoben werden.',
    close: 'Auswahl schließen',
    /** The slot, and only the slot. Whether the session took place is the
     *  activity's status. */
    status: {
      requested: 'Angefragt',
      planned: 'Geplant',
      confirmed: 'Bestätigt',
      cancelled: 'Abgesagt',
      cancelled_late: 'Kurzfristig abgesagt',
    },
    allStatuses: 'Alle',
    /** The distinction the slot finder makes — it was the exclusion
     *  constraint's until migration 0034 dropped it, and the sentence is the
     *  same one because to the practitioner it means the same thing: whether
     *  this time will be offered again. */
    releasesSlot: 'Der Zeitraum wird dadurch wieder frei.',
    holdsSlot: 'Der Zeitraum bleibt belegt.',
  },
  mail: {
    title: 'Mailversand',
    accountTitle: 'Mailkonto',
    description:
      'Über dieses Konto werden Rechnungen versendet. Das Passwort wird verschlüsselt ' +
      'gespeichert und niemals zurückgegeben.',
    host: 'Server',
    port: 'Port',
    security: 'Verschlüsselung',
    securities: {
      starttls: 'STARTTLS (üblich, Port 587)',
      tls: 'TLS (Port 465)',
      none: 'Keine',
    },
    username: 'Benutzername',
    password: 'Passwort',
    /** The field's placeholder once a password is stored — read mode shows
     *  dots instead, this is what the empty, editable field says. */
    passwordPlaceholder: 'unverändert lassen',
    passwordClear: 'Passwort entfernen',
    fromAddress: 'Absenderadresse',
    fromName: 'Absendername',
    saved: 'Mailkonto gespeichert.',
    notConfigured: 'Es ist noch kein Mailkonto hinterlegt.',
    remove: 'Mailkonto entfernen',
    removeTitle: 'Mailkonto entfernen?',
    /** Names what cannot be typed back in: the password is stored encrypted and
     *  the API never returns it, so it goes with the row for good. */
    removeBody:
      'Server, Absenderadresse und Passwort werden gelöscht. Das Passwort lässt sich nicht ' +
      'wiederherstellen — es müsste neu eingegeben werden. Rechnungen lassen sich danach ' +
      'nicht mehr versenden.',
    removed: 'Mailkonto entfernt.',

    test: 'Testmail senden',
    /** The safeguard, said out loud where the button is. */
    testHint:
      'Die Testmail geht ausschließlich an die Absenderadresse. Eine andere Adresse lässt ' +
      'sich hier nicht eintragen.',
    testOk: (recipient: string) => `Testmail an ${recipient} versendet.`,
    testFailed: 'Die Testmail wurde nicht angenommen.',

    templates: 'Mailvorlagen',
    templatesHint:
      'Betreff und Text für den Rechnungsversand. Beides lässt sich vor dem Absenden ' +
      'noch ändern.',
    templateName: 'Name',
    templateSubject: 'Betreff',
    templateBody: 'Text',
    templateDefault: 'Standard',
    templateActive: 'Aktiv',
    templateNew: 'Neue Vorlage',
    templateEmpty: 'Noch keine Mailvorlagen.',
    templateSaved: 'Vorlage gespeichert.',
    templateRemoved: 'Vorlage gelöscht.',
    templateRemoveTitle: 'Vorlage löschen?',
    templateRemoveBody: 'Die Mailvorlage wird endgültig gelöscht.',
    /** Betreff and Text share this closed set — never the number-range
     *  prefix placeholders (YYYY/MM/Q), a different list for a different
     *  field (README's own warning, D4). */
    placeholderPrompt: 'Im Betreff und im Text lassen sich Platzhalter verwenden.',
    viewPlaceholders: 'Platzhalter ansehen',
    placeholderDialogTitle: 'Platzhalter',
    placeholderDialogDescription: 'Werden beim Versand durch die Werte der Rechnung ersetzt.',
    placeholderList: [
      { token: '{{number}}', meaning: 'Rechnungsnummer' },
      { token: '{{date}}', meaning: 'Rechnungsdatum' },
      { token: '{{total}}', meaning: 'Gesamtbetrag' },
      { token: '{{name}}', meaning: 'Empfängername' },
    ],

    send: 'Per Mail senden',
    /** Why the recipient field opened empty. Shown only while it still is —
     *  it explains a gap and is pointless once one is typed in. */
    noRecipientAddress:
      'Für diesen Empfänger ist keine E-Mail-Adresse hinterlegt. Tragen Sie sie hier von Hand ' +
      'ein oder ergänzen Sie sie am Kontakt.',
    noTemplate:
      'Es ist keine Mailvorlage hinterlegt. Betreff und Text lassen sich hier von Hand ' +
      'schreiben; eine Vorlage legen Sie in den Einstellungen an.',
    template: 'Vorlage',
    templateChanged: 'Betreff und Text wurden nicht ersetzt, weil Sie sie bereits geändert haben.',
    templateApply: 'Vorlage übernehmen',
    sendTitle: 'Rechnung versenden',
    sendDescription: 'Die Rechnung geht als PDF-Anhang an diese Adresse.',
    recipient: 'Empfänger',
    recipientHint: 'Vorbelegt mit dem Rechnungsempfänger, falls hinterlegt. Änderbar.',
    subject: 'Betreff',
    body: 'Text',
    sending: 'Wird gesendet …',
    sendNow: 'Senden',
    sent: (recipient: string) => `Rechnung an ${recipient} versendet.`,
    sendFailed: 'Die Mail wurde nicht angenommen.',
    /** Left standing rather than emptied — and pointed at here, so it is
     *  noticed before the recipient notices it. */
    unknownPlaceholders: (names: string[]) =>
      names.length === 1
        ? `Im Text steht ein unbekannter Platzhalter: {{${names[0]}}}. Er wird unverändert mitgesendet.`
        : `Im Text stehen unbekannte Platzhalter: ${names.map((name) => `{{${name}}}`).join(', ')}. ` +
          'Sie werden unverändert mitgesendet.',

    history: 'Versendet',
    historyEmpty: 'Noch nicht versendet.',
    historyOk: 'zugestellt an den Server',
    historyFailed: 'fehlgeschlagen',
  },
  /**
   * The Lemniscus import (Einstellungen → Datenimport). No sentence addresses
   * the reader. An issue of the preview travels as a code
   * (`lemniscusIssueCodes` in `packages/shared`) and gets its sentence here,
   * one per code — `lemniscus-import.test.ts` walks the list, so a code added
   * without a sentence fails there.
   */
  dataImport: {
    title: 'Datenimport',
    description:
      'Übernimmt einen Export aus Lemniscus: die Akten als Kontakte, die Termine als Vorgänge ' +
      'mit Termin, die Notizen mit ihren Anhängen und die Rechnungen als PDF. Der Import ist ' +
      'ein einmaliger Umzug — er ist nur möglich, solange keine Kontakte angelegt sind, und ' +
      'schreibt alles oder nichts.',
    closed:
      'Es sind bereits Kontakte angelegt. Ein Import ist nur in eine Praxis ohne Kontakte möglich.',

    filesTitle: 'Dateien',
    filesHint: (maxSize: string) =>
      `Beide Dateien aus dem Export von Lemniscus, je höchstens ${maxSize}.`,
    fileLabel: { archive: 'Akten (ZIP-Datei)', records: 'Aktenliste (Excel-Datei)' },
    fileMissing: 'Noch nicht hochgeladen.',
    fileChoose: 'Datei wählen',
    fileReplace: 'Ersetzen',
    fileCancel: 'Abbrechen',
    fileUploading: (percent: number) => `Wird hochgeladen … ${percent} %`,
    fileUploaded: (size: string, when: string) => `${size}, hochgeladen am ${when}`,
    fileTooLarge: (maxSize: string) => `Die Datei ist größer als ${maxSize}.`,
    discardTitle: 'Hochgeladene Dateien löschen?',
    discardBody:
      'Beide Dateien werden vom Server entfernt. Importiert wurde nichts; für einen Import ' +
      'werden sie erneut hochgeladen.',
    discarded: 'Die Dateien wurden gelöscht.',

    previewTitle: 'Vorschau',
    previewLoading: 'Der Export wird gelesen …',
    previewNeedsFiles: 'Die Vorschau erscheint, sobald beide Dateien hochgeladen sind.',
    count: {
      records: 'Akten',
      persons: 'Personen',
      organizations: 'Unternehmen',
      appointmentsPast: 'vergangene Termine',
      appointmentsFuture: 'künftige Termine',
      notes: 'Notizen',
      invoices: 'Rechnungen',
      files: 'Dateien',
      /** Shown only where the export's sheet "Termine" gave any (#67). */
      appointmentRemarks: 'Terminanmerkungen',
    },
    filesSize: (size: string) => `${size} entpackt`,
    /** Beside the number of notes: how many of them document an appointment
     *  and are linked to its Vorgang, and how many get their text from the
     *  sheet "Notizen" (#67). Each only where there are any. */
    notesDetail: (parts: string[]) => `davon ${parts.join(', ')}`,
    notesLinked: (count: number) => `${count} einem Vorgang zugeordnet`,
    notesWithText: (count: number) => `${count} mit Text`,

    mappingTitle: 'Zuordnungen',
    mappingHint:
      'Jeder Wert aus dem Export braucht einen Eintrag aus praxi. Fehlt einer, wird er vorher ' +
      'in den Einstellungen angelegt — die hochgeladenen Dateien bleiben dabei erhalten.',
    mapping: {
      recordTypes: 'Akten-Typ → Rolle',
      genders: 'Geschlecht',
      appointmentNames: 'Terminbezeichnung → Vorgangsart',
    },
    mappingEmptyValue: '(ohne Bezeichnung)',
    mappingCount: (count: number) => `${count}×`,
    mappingUnset: 'Nicht zugeordnet',
    noteType: 'Notizart der Notizen',
    invoiceNoteType: 'Notizart der Rechnungen',
    invoiceNoteTypeHint: 'Jede Rechnung wird eine eigene Notiz mit der PDF als Anhang.',

    errorsTitle: (count: number) => (count === 1 ? '1 Fehler' : `${count} Fehler`),
    errorsHint: 'Solange Fehler bestehen, wird nichts importiert.',
    hintsTitle: (count: number) => (count === 1 ? '1 Hinweis' : `${count} Hinweise`),
    hintsHint: 'Hinweise stehen dem Import nicht im Weg.',
    issuesMore: (count: number) => `… und ${count} weitere.`,
    issueRecord: (number: string | null, name: string | null) =>
      number === null
        ? (name ?? 'Akte')
        : name === null
          ? `Akte ${number}`
          : `Akte ${number} (${name})`,
    issue: {
      sheet_unreadable: () =>
        'Die Excel-Datei lässt sich nicht lesen oder enthält kein Blatt „Akten“.',
      sheet_empty: () => 'Das Blatt „Akten“ enthält keine Akte.',
      sheet_missing_column: (issue) => `Im Blatt „Akten“ fehlt die Spalte „${issue.field}“.`,
      archive_unreadable: (issue) =>
        ({
          not_a_zip: 'Die ZIP-Datei lässt sich nicht lesen.',
          encrypted: 'Die ZIP-Datei ist verschlüsselt.',
          compression: 'Die ZIP-Datei nutzt ein Kompressionsverfahren, das nicht unterstützt wird.',
          symlink: 'Die ZIP-Datei enthält eine Verknüpfung (Symlink) und wird deshalb abgelehnt.',
          too_many_entries: 'Die ZIP-Datei enthält zu viele Einträge.',
          path_too_long: 'Die ZIP-Datei enthält einen überlangen Dateinamen.',
          no_records: 'Die ZIP-Datei enthält keinen Aktenordner.',
        })[issue.value ?? ''] ?? 'Die ZIP-Datei lässt sich nicht lesen.',
      number_invalid: () => 'Die Nummer fehlt oder ist keine ganze Zahl ab 1.',
      number_duplicate: () => 'Die Nummer kommt in der Excel-Datei mehrfach vor.',
      folder_without_record: () => 'Zu diesem Ordner gibt es keine Zeile in der Excel-Datei.',
      folder_duplicate: () => 'Zu dieser Nummer gibt es mehr als einen Ordner in der ZIP-Datei.',
      name_mismatch: (issue) =>
        `Der Ordner „${issue.value}“ trägt einen anderen Namen als die Akte mit dieser Nummer.`,
      required_missing: (issue) => `„${issue.field}“ fehlt.`,
      field_too_long: (issue) =>
        `${issue.line === null ? '' : `Termine, Zeile ${issue.line}: `}„${issue.field}“ ist länger, als praxi speichern kann.`,
      date_invalid: (issue) =>
        `${issue.line === null ? '' : `Termine, Zeile ${issue.line}: `}„${issue.field}“ ist kein gültiger Wert${issue.value ? ` (${issue.value})` : ''}.`,
      csv_unreadable: () => 'Die Datei behandlungsverlauf.csv lässt sich nicht lesen.',
      csv_missing_column: (issue) => `In behandlungsverlauf.csv fehlt die Spalte „${issue.field}“.`,
      duration_invalid: (issue) =>
        `Termine, Zeile ${issue.line}: Die Dauer „${issue.value}“ ist keine gültige Minutenzahl.`,
      duration_missing: (issue) =>
        `Termine, Zeile ${issue.line}: Die Dauer fehlt, und die gewählte Vorgangsart hat keine Vorgabedauer.`,
      record_type_unmapped: (issue) =>
        `Dem Akten-Typ „${issue.value}“ ist noch keine Rolle zugeordnet.`,
      gender_unmapped: (issue) =>
        `Dem Geschlecht „${issue.value}“ ist noch kein Eintrag zugeordnet.`,
      appointment_name_unmapped: (issue) =>
        issue.value
          ? `Der Terminbezeichnung „${issue.value}“ ist noch keine Vorgangsart zugeordnet.`
          : 'Den Terminen ohne Bezeichnung ist noch keine Vorgangsart zugeordnet.',
      note_type_unmapped: (issue) =>
        issue.field === 'invoiceNoteTypeId'
          ? 'Für die Rechnungen ist noch keine Notizart gewählt.'
          : 'Für die Notizen ist noch keine Notizart gewählt.',
      file_too_large: (issue) => `Die Datei „${issue.value}“ ist größer, als ein Anhang sein kann.`,
      unpacked_too_large: () => 'Der Export ist entpackt größer als erlaubt.',
      disk_space: () => 'Auf dem Server ist nicht genug Speicherplatz für den Export frei.',
      record_without_folder: () =>
        'Kein Ordner in der ZIP-Datei — es werden nur die Stammdaten übernommen.',
      attachment_without_note: () =>
        'Anhänge ohne zugehörige Notiz-PDF — die Notiz wird trotzdem angelegt.',
      invoice_without_pdf: (issue) =>
        `Termine, Zeile ${issue.line}: Die Rechnung zu diesem Termin ist nicht im Export enthalten.`,
      invoice_without_appointment: (issue) =>
        `Die Rechnung ${issue.value} gehört zu keinem Termin; sie wird trotzdem übernommen.`,
      attachment_over_limit: (issue) =>
        issue.count === 1
          ? '1 Anhang ist größer als die Grenze für das Hochladen; er wird trotzdem übernommen.'
          : `${issue.count} Anhänge sind größer als die Grenze für das Hochladen; sie werden trotzdem übernommen.`,
      field_not_imported: (issue) =>
        `„${issue.field}“ wird nicht übernommen (ausgefüllt in ${issue.count === 1 ? '1 Akte' : `${issue.count} Akten`}).`,
      email_invalid: (issue) =>
        `Die E-Mail-Adresse „${issue.value}“ ist ungültig; das Feld bleibt leer.`,
      country_unknown: (issue) =>
        `Das Land „${issue.value}“ steht nicht in der Länderliste; das Feld bleibt leer.`,
      unknown_file: (issue) =>
        issue.count === 1
          ? '1 Datei der ZIP-Datei gehört zu keiner Notiz und keiner Rechnung; sie wird nicht übernommen.'
          : `${issue.count} Dateien der ZIP-Datei gehören zu keiner Notiz und keiner Rechnung; sie werden nicht übernommen.`,
      empty_file: (issue) =>
        issue.count === 1
          ? '1 leere Datei wird nicht übernommen.'
          : `${issue.count} leere Dateien werden nicht übernommen.`,
      /* A note that documents an appointment is linked to that appointment's
         Vorgang by the "Termin" line of its PDF (#60). Where that fails the
         note is imported all the same, so each of the three says so. */
      note_appointment_not_found: (issue) =>
        `Eine Notiz nennt den Termin ${issue.value}; zu diesem Zeitpunkt beginnt kein Termin der Akte. Sie wird ohne Zuordnung zu einem Vorgang übernommen.`,
      note_appointment_ambiguous: (issue) =>
        `Eine Notiz nennt den Termin ${issue.value}; zu diesem Zeitpunkt beginnen mehrere Termine der Akte. Sie wird ohne Zuordnung zu einem Vorgang übernommen.`,
      note_appointment_unreadable: (issue) =>
        `Die Terminangabe einer Notiz („${issue.value}“) ist kein Datum mit Uhrzeit. Sie wird ohne Zuordnung zu einem Vorgang übernommen.`,
      /* The sheets "Notizen" and "Termine" (#67) are optional, and nothing in
         them stops an import: a row that fits no note or appointment, or more
         than one, is left out, and these say how many. */
      extra_sheet_missing_column: (issue) =>
        `Im Blatt „${issue.value}“ fehlt die Spalte „${issue.field}“; das Blatt wird nicht genutzt.`,
      note_text_unmatched: (issue) =>
        issue.count === 1
          ? '1 Text aus dem Blatt „Notizen“ passt zu keiner Notiz-PDF der Akte (Titel und Zeitpunkt); er wird nicht übernommen.'
          : `${issue.count} Texte aus dem Blatt „Notizen“ passen zu keiner Notiz-PDF der Akte (Titel und Zeitpunkt); sie werden nicht übernommen.`,
      note_text_ambiguous: (issue) =>
        issue.count === 1
          ? '1 Text aus dem Blatt „Notizen“ lässt sich keiner Notiz-PDF eindeutig zuordnen; er wird nicht übernommen.'
          : `${issue.count} Texte aus dem Blatt „Notizen“ lassen sich keiner Notiz-PDF eindeutig zuordnen; sie werden nicht übernommen.`,
      note_text_too_long: (issue) =>
        issue.count === 1
          ? '1 Text aus dem Blatt „Notizen“ ist länger, als eine Notiz sein kann; er wird nicht übernommen, die PDF bleibt als Anhang.'
          : `${issue.count} Texte aus dem Blatt „Notizen“ sind länger, als eine Notiz sein kann; sie werden nicht übernommen, die PDFs bleiben als Anhang.`,
      appointment_remark_unmatched: (issue) =>
        issue.count === 1
          ? '1 Anmerkung aus dem Blatt „Termine“ passt zu keinem Termin der Akte; sie wird nicht übernommen.'
          : `${issue.count} Anmerkungen aus dem Blatt „Termine“ passen zu keinem Termin der Akte; sie werden nicht übernommen.`,
      appointment_remark_ambiguous: (issue) =>
        issue.count === 1
          ? '1 Anmerkung aus dem Blatt „Termine“ lässt sich keinem Termin eindeutig zuordnen; sie wird nicht übernommen.'
          : `${issue.count} Anmerkungen aus dem Blatt „Termine“ lassen sich keinem Termin eindeutig zuordnen; sie werden nicht übernommen.`,
      appointment_remark_too_long: (issue) =>
        issue.count === 1
          ? '1 Anmerkung aus dem Blatt „Termine“ passt nicht mehr in die interne Notiz des Vorgangs; sie wird nicht übernommen.'
          : `${issue.count} Anmerkungen aus dem Blatt „Termine“ passen nicht mehr in die interne Notiz ihres Vorgangs; sie werden nicht übernommen.`,
      appointment_diagnosis_not_imported: (issue) =>
        `„Diagnose“ im Blatt „Termine“ wird nicht übernommen; ein Vorgang hat kein Feld dafür (ausgefüllt bei ${issue.count === 1 ? '1 Termin' : `${issue.count} Terminen`}).`,
    } satisfies Record<LemniscusIssueCode, (issue: LemniscusIssue) => string>,

    start: 'Importieren',
    startBlocked: 'Möglich, sobald keine Fehler mehr bestehen.',
    confirmTitle: 'Import starten?',
    confirmBody:
      'Der Import schreibt alle Akten in einem Durchgang und lässt sich nicht rückgängig ' +
      'machen. Danach ist kein weiterer Import möglich.',
    confirm: 'Import starten',

    runningTitle: 'Der Import läuft',
    runningHint: 'Der Import läuft auf dem Server weiter, auch wenn diese Seite verlassen wird.',
    phase: {
      checking: 'Export prüfen',
      unpacking: 'Dateien entpacken',
      contacts: 'Kontakte schreiben',
      appointments: 'Termine und Vorgänge schreiben',
      notes: 'Notizen schreiben',
      moving: 'Dateien ablegen',
    },
    phaseProgress: (done: number, total: number) => `${done} von ${total}`,

    completedTitle: 'Import abgeschlossen',
    completedAt: (when: string) => `Abgeschlossen am ${when}.`,
    completedWithoutCounts:
      'Der Import wurde abgeschlossen. Die Anzahlen liegen nach einem Neustart des Servers nicht vor.',
    result: {
      contacts: 'Kontakte',
      appointments: 'Vorgänge mit Termin',
      notes: 'Notizen',
      invoices: 'Rechnungen',
      files: 'Dateien',
    },
    googlePending: (count: number) =>
      count === 1
        ? '1 Termin wartet noch auf die Übertragung in den Google-Kalender.'
        : `${count} Termine warten noch auf die Übertragung in den Google-Kalender.`,
    failed: 'Der letzte Import ist fehlgeschlagen. Es wurde nichts importiert.',
    failedErrorId: (errorId: string) => `Fehler-ID: ${errorId}`,
    aborted:
      'Der letzte Import wurde durch einen Neustart des Servers abgebrochen. Es wurde nichts ' +
      'importiert; er kann erneut gestartet werden.',
  },
  google: {
    title: 'Google-Kalender',
    /** Says what is fixed, not what the title happens to be (B1). It used to
     *  promise "mit der Kontaktnummer statt mit dem Namen", which the template
     *  below can now falsify — and a screen that states a rule the software no
     *  longer keeps is worse than one that states none. What is still true of
     *  every event whatever the template says is the shape: two times, one
     *  status, one title. */
    description:
      'Ein Eintrag im Google-Kalender trägt Anfang, Ende, ob er abgesagt ist — und einen ' +
      'Titel, den Sie unten festlegen. Mehr wird nicht übertragen. Die Praxisdatenbank ' +
      'bleibt das führende System; der Google-Kalender ist eine Projektion.',
    notConfigured:
      'Nicht eingerichtet. In der Umgebung fehlen GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET ' +
      'oder ENCRYPTION_KEY.',
    notConnected: 'Nicht verbunden.',
    connectedAs: 'Verbunden als',
    connect: 'Mit Google verbinden',
    connectHint:
      'Die Anmeldung öffnet sich in einem neuen Fenster. Danach kehren Sie hierher zurück.',
    disconnect: 'Verbindung trennen',
    disconnectTitle: 'Verbindung zu Google trennen',
    disconnectQuestion: 'Was soll mit den Terminen geschehen, die bereits in Google stehen?',
    disconnectKeep: 'Stehen lassen',
    disconnectKeepHint: 'Nichts geht verloren. Die Einträge bleiben, wo sie sind.',
    disconnectDelete: 'In Google löschen',
    disconnectDeleteHint:
      'Es werden nur Termine gelöscht, die diese Software dort angelegt hat. ' +
      'Was sich nicht löschen lässt, wird Ihnen danach mit Datum und Uhrzeit genannt.',
    disconnected: 'Verbindung getrennt.',
    disconnectedWithDeletions: (deleted: number, attempted: number) =>
      `Verbindung getrennt. ${deleted} von ${attempted} Terminen in Google gelöscht.`,
    disconnectRemaining: 'Diese Termine stehen weiterhin in Google und müssen von Hand weg:',
    keyMismatch:
      'Der hinterlegte Schlüssel passt nicht zum gespeicherten Token. Setzen Sie den ' +
      'ursprünglichen ENCRYPTION_KEY zurück oder verbinden Sie neu.',
    practiceCalendar: 'Praxiskalender',
    practiceCalendarHint:
      'Hierhin werden Termine geschrieben. Am besten ein eigener Kalender, kein privater.',
    practiceCalendarNone: 'Keiner — es wird nichts geschrieben',
    practiceCalendarReadOnly: 'nur lesbar',
    /** The title template (B1). It replaced a checkbox that could say
     *  "Kontaktnummer" or "Name" and nothing in between. */
    eventTitle: 'Titel der Termine',
    /** The sentence this whole section turns on: the title is not one field
     *  among several, it is the entire content of the projection. */
    eventTitleLead:
      'Der Titel ist alles, was Google erfährt. Ein Eintrag trägt sonst nur Anfang, Ende und ' +
      'ob er abgesagt ist — keine Beschreibung, keine Teilnehmer, keinen Ort.',
    eventTitleTemplate: 'Vorlage',
    eventTitlePreset: 'Vorlage wählen',
    eventTitleCustom: 'Eigene Vorlage',
    eventTitlePresets: [
      { template: '{{contactNumber}}', label: 'Nur die Kontaktnummer' },
      { template: '{{contactNumber}} — {{activityType}}', label: 'Kontaktnummer und Vorgangsart' },
      { template: '{{contactName}}', label: 'Name des Kontakts' },
      { template: '{{contactName}} — {{activityType}}', label: 'Name und Vorgangsart' },
    ],
    /** With made-up values, so nobody has to wonder whether the preview is
     *  showing a real patient. */
    eventTitlePreview: 'So steht es in Google:',
    eventTitlePreviewHint: 'Beispielwerte — der echte Termin setzt seine eigenen ein.',
    /** The preflight, said where it is refused. */
    eventTitleEmpty:
      'Diese Vorlage ergibt keinen Titel. Google würde „(kein Titel)" anzeigen, deshalb wird ' +
      'sie nicht gespeichert.',
    eventTitleUnknown: (names: string[]) =>
      names.length === 1
        ? `„{{${names[0]}}}" gibt es nicht. Unbekannte Platzhalter werden nicht gespeichert — ` +
          'sie würden sonst wörtlich in Googles Kalender stehen.'
        : `Diese Platzhalter gibt es nicht: ${names.map((name) => `{{${name}}}`).join(', ')}. ` +
          'Unbekannte Platzhalter werden nicht gespeichert — sie würden sonst wörtlich in ' +
          'Googles Kalender stehen.',
    /** Fires when the template needs a Vorgang and the appointment has none —
     *  which is a real state, not a hypothetical: a blocker with a contact on
     *  it, or an appointment documented without one. */
    eventTitleSometimesEmpty:
      'Bei einem Termin ohne Vorgang ergibt diese Vorlage keinen Titel. Solche Termine gehen ' +
      'dann nicht zu Google — sie bleiben in der Warteschlange stehen, bis die Vorlage etwas ' +
      'ergibt.',
    eventTitlePlaceholderList: [
      { token: '{{contactNumber}}', meaning: 'Kontaktnummer' },
      { token: '{{contactName}}', meaning: 'Name des Kontakts' },
      { token: '{{activityType}}', meaning: 'Bezeichnung der Vorgangsart' },
      { token: '{{activityTitle}}', meaning: 'Titel des Vorgangs' },
      { token: '{{appointmentTitle}}', meaning: 'Titel des Termins' },
    ],
    /** The chain, which is not obvious from a list of names. */
    eventTitleChainHint:
      'Mit „{{activityTitle | appointmentTitle}}" wird der erste genommen, der einen Wert hat.',
    /** Rule 13: a rewrite of what already went out could never be complete —
     *  the data has long since been cached on a phone. */
    eventTitleFuture:
      'Die Vorlage gilt nur für künftige Termine. Was bereits in Google steht, bleibt ' +
      'unverändert.',
    /** Because the row holding it is deleted with the connection, and finding
     *  the setting back at the number after reconnecting would otherwise be a
     *  puzzle. */
    eventTitleReset:
      'Beim Trennen der Verbindung wird die Vorlage zurückgesetzt — ein neuer Zugang ' +
      'überträgt wieder nur Kontaktnummern.',
    /** A contact-less appointment is asked before the template is looked at. */
    eventTitleBare:
      'Termine ohne Kontakt — Blocker, Dokumentationszeit, Besprechungen — gehen unabhängig ' +
      'von der Vorlage als „Belegt" hinaus.',
    freebusyCalendars: 'Kalender für die Belegtzeiten',
    freebusyCalendarsHint:
      'Beim Planen werden aus diesen Kalendern nur die belegten Zeiträume abgefragt — ' +
      'keine Titel, keine Teilnehmer. Das Zugriffsrecht lässt nichts anderes zu.',
    lastSync: 'Letzte Synchronisation',
    never: 'noch nie',
    queue: 'Warteschlange',
    queueEmpty: 'nichts offen',
    queuePending: (count: number) => `${count} offen`,
    queueStuck: (count: number) => `${count} hängen fest`,
    lastError: 'Letzter Fehler',
    syncNow: 'Jetzt synchronisieren',
    syncResult: (result: { pushed: number; failed: number; pulled: number }) =>
      `${result.pushed} gesendet, ${result.failed} fehlgeschlagen, ${result.pulled} übernommen.`,
    saved: 'Einstellung gespeichert.',
    /** The busy blocks from the private calendars, painted behind the entries. */
    busyLegend: 'Privat belegt',
    conflictsBanner: (count: number) =>
      count === 1
        ? 'Ein Termin wurde auf beiden Seiten geändert.'
        : `${count} Termine wurden auf beiden Seiten geändert.`,
    conflictsOpen: 'Ansehen',
    conflictsTitle: 'Auf beiden Seiten geändert',
    conflictsDescription:
      'Diese Termine wurden hier und in Google geändert. Es wird nichts zusammengeführt — ' +
      'wählen Sie je Termin, welche Fassung gilt.',
    conflictLocal: 'Hier',
    conflictRemote: 'In Google',
    conflictKeepLocal: 'Diese Fassung behalten',
    conflictKeepRemote: 'Google übernehmen',
    conflictCancelled: 'abgesagt',
    conflictResolved: 'Konflikt aufgelöst.',
    conflictWithoutContact: 'Termin ohne Kontakt',
    contactNumberShort: 'Kontakt',
  },
  /** The catalogue behind a note's type (L1). Its own section in `strings`
   *  rather than under `note`, because it is a settings screen and not part of
   *  the documentation. */
  noteType: {
    title: 'Notizarten',
    hint: 'wonach eine Notiz abgelegt wird',
    create: 'Neue Notizart',
    empty: 'Noch keine Notizart angelegt.',
    label: 'Bezeichnung',
    labelHint: 'Wird an jeder Notiz dieser Art angezeigt und lässt sich jederzeit ändern.',
    showAsTab: 'Als Filter über der Notizliste',
    deleteTitle: 'Notizart löschen?',
    deleteBody:
      'Die Notizart wird aus der Auswahl entfernt. Notizen, die sie tragen, verhindern das — ' +
      'bei gesperrten Notizen endgültig.',
    /** Shown only while the list is EMPTY (B1, F2). It states a restriction,
     *  and while there are types the restriction does not bite — a sentence
     *  that explains a case which is not the case is noise under every card.
     *  What went with it: that a new note starts on the first entry (a detail
     *  of the note dialog, not of this list) and that an addendum is not a
     *  type (true, and answering a question nobody asked here). */
    footerEmpty: 'Ohne Notizart lässt sich keine Notiz schreiben.',
  },
  note: {
    title: 'Notizen',
    create: 'Neue Notiz',
    empty: 'Noch keine Notizen.',

    noteDate: 'Datum',
    type: 'Art',
    /** The labels themselves come from the catalogue and travel with the note
     *  (L1); what is left here is the case where there are none. */
    typesEmpty: 'Es sind keine Notizarten angelegt.',
    typesEmptyHint:
      'Ohne Notizart lässt sich keine Notiz schreiben. Notizarten werden in den ' +
      'Einstellungen unter „Notizarten“ angelegt.',
    text: 'Text',
    activity: 'Zum Vorgang',
    activityNone: 'Kein Vorgang',
    /** Named where it bites, and only there — see `activityChoicesQueryOptions`. */
    activityCapped: (n: number) =>
      `Nur die letzten ${n} Vorgänge werden geladen. Notizen zu älteren Vorgängen entstehen ` +
      'im Reiter Vorgänge.',
    /** What an addendum is headed with, in the row and in the reading pane —
     *  where every other note says its type. `writeAddendum` is the button
     *  that starts one; the same word, a different job. */
    addendumLabel: 'Nachtrag',

    titleLabel: 'Titel',
    /** Under the title field while it is empty — the reason both save
     *  buttons are greyed out, said where the gap is (N1b). */
    titleRequired: 'Die Notiz braucht einen Titel, auch als Entwurf.',
    save: 'Speichern',
    /** Keeps the note a draft. "Speichern" makes it final. */
    saveDraft: 'Als Entwurf speichern',
    undo: 'Rückgängig',
    redo: 'Wiederholen',
    undoShortcutApple: '⌘Z',
    redoShortcutApple: '⇧⌘Z',
    undoShortcutOther: 'Strg+Z',
    redoShortcutOther: 'Strg+Umschalt+Z',
    saving: 'Wird gespeichert …',
    cancel: 'Abbrechen',
    saved: 'Notiz gespeichert.',
    savedDraft: 'Entwurf gespeichert.',
    saveFailed: 'Die Notiz konnte nicht gespeichert werden.',
    edit: 'Bearbeiten',
    remove: 'Löschen',
    removeTitle: 'Notiz löschen?',
    removeBody:
      'Die Notiz und ihre Anhänge werden gelöscht. Das lässt sich nicht rückgängig machen.',
    removed: 'Notiz gelöscht.',
    /** A note is started on the server before its form opens (N1b); when that
     *  fails, there is no form, only this. */
    startFailed: 'Die Notiz konnte nicht angelegt werden.',
    /** "Abbrechen" with something changed (N1b). The note stays as it was
     *  last saved; what goes is what was typed since. */
    discardTitle: 'Änderungen verwerfen?',
    discardBody:
      'Die Notiz bleibt im zuletzt gespeicherten Stand. Alle Änderungen seitdem gehen ' +
      'verloren, auch der automatisch gesicherte Stand.',
    discardKeep: 'Weiter bearbeiten',
    discardConfirm: 'Verwerfen',
    leaveChangesTitle: 'Ungespeicherte Änderungen',
    leaveChangesBody: 'Die Notiz enthält Änderungen, die noch nicht gespeichert sind.',
    leaveUploadsNote: 'Laufende Uploads werden beim Verlassen abgebrochen.',
    leaveUploadsTitle: 'Uploads laufen noch',
    leaveUploadsBody:
      'Beim Verlassen werden laufende und wartende Uploads abgebrochen. Bereits hochgeladene Dateien bleiben erhalten.',
    leaveStay: 'Bleiben',
    leaveDiscard: 'Verwerfen',
    leaveSave: 'Speichern und verlassen',
    leaveGo: 'Verlassen',
    leaveNoTitle: 'Ohne Titel lässt sich nicht speichern.',
    leaveNoDate: 'Ohne Datum lässt sich nicht speichern.',
    lockAfterUpload: 'Erst nach dem Upload möglich.',

    /** The one action in this application that cannot be taken back. Say so
     *  plainly — rule 7 in `.claude/rules/notes.md`. */
    lock: 'Sperren',
    lockTitle: 'Notiz sperren?',
    lockBody:
      'Nach dem Sperren lässt sich diese Notiz nie wieder ändern oder löschen — von ' +
      'niemandem, auch nicht über die Datenbank. Angehängte Dateien ebenso wenig, und es ' +
      'kann keine weitere Datei mehr hinzukommen. Korrekturen sind danach nur noch als ' +
      'Nachtrag möglich, der sichtbar unter der Notiz steht.',
    lockConfirm: 'Endgültig sperren',
    locked: 'Notiz gesperrt.',
    lockFailed: 'Die Notiz konnte nicht gesperrt werden.',
    lockedAt: 'Gesperrt am',
    addendumTo: 'Nachtrag zu',
    /** The three statuses (N1), as badge and chip alike. */
    status: { draft: 'Entwurf', final: 'Fertig', locked: 'Gesperrt' },

    /* The autosave (L2). It is offered, never restored by itself: what stands
       on screen has to be what was chosen. Not called "Entwurf" — that is a
       status of the note since N1, and the two must not be mistaken. */
    autosaveFound: (when: string) =>
      `Ein automatisch gesicherter, nicht gespeicherter Stand von ${when} liegt vor.`,
    autosaveAccept: 'Übernehmen',
    autosaveDiscard: 'Verwerfen',
    writeAddendum: 'Nachtrag',

    files: 'Anhänge',
    filesEmpty: 'Keine Anhänge.',
    /** The upload component (N4): a drop zone with a picker in it, and one
     *  row per file while it waits, runs, or failed. */
    fileDropHint: 'Dateien hierher ziehen oder',
    fileChoose: 'Dateien auswählen',
    fileQueued: 'Wartet',
    fileProgress: (percent: number) => `${percent} %`,
    fileCancel: 'Upload abbrechen',
    fileDismiss: 'Meldung schließen',
    fileFolderRejected: 'Ordner lassen sich nicht hochladen, nur einzelne Dateien.',
    fileFailed: 'Die Datei konnte nicht angehängt werden.',
    fileRemove: 'Datei löschen',
    fileRemoveTitle: 'Datei löschen?',
    /** Names the file: a note can carry several, and "die Datei" would not say
     *  which one is going. */
    fileRemoveBody: (fileName: string) =>
      `„${fileName}" wird endgültig gelöscht. Das lässt sich nicht rückgängig machen.`,
    fileRemoved: 'Datei gelöscht.',
    fileDownload: 'Herunterladen',
    /** The folder view and its preview (N5). */
    filePreviewPosition: (index: number, count: number) => `${index} / ${count}`,
    filePreviewReduced: 'Verkleinerte Vorschau.',
    fileNoPreview: 'Für diesen Dateityp gibt es keine Vorschau.',
    filePrevious: 'Vorheriger Anhang',
    fileNext: 'Nächster Anhang',
    /** Every type is accepted since N2; the limit is the server's
     *  `NOTE_FILE_MAX_MB`, asked for rather than written down here. */
    fileHint: (maxMb: number) =>
      `Jeder Dateityp, höchstens ${maxMb} MB. ` +
      'Anhänge sind Teil der Notiz und werden beim Sperren mit festgeschrieben.',
    fileTooLarge: (maxMb: number) => `Die Datei ist zu groß. Erlaubt sind höchstens ${maxMb} MB.`,
    /** A 413 that did not come from the API — a proxy in front of it answers
     *  without our JSON, and then the limit is not known. */
    fileTooLargeUnknownLimit: 'Die Datei ist zu groß für den Upload.',
    /** The small Markdown a note may carry (D10). Five constructs, and the
     *  hint names all of them — the syntax is typed faster than it is clicked
     *  once one knows it. */
    /** The editor's own vocabulary (L6a) — one name per action, read by the
     *  selection toolbar, the slash menu and the plus in the gutter alike, so
     *  the three cannot call one thing three things. */
    formatBold: 'Fett',
    formatItalic: 'Kursiv',
    formatStrike: 'Durchgestrichen',
    formatCode: 'Code',
    formatLink: 'Link',
    linkAddress: 'Adresse',
    linkApply: 'Übernehmen',
    linkRemove: 'Link entfernen',
    blockParagraph: 'Text',
    blockHeading: (level: number) => `Überschrift ${level}`,
    blockBullets: 'Aufzählung',
    blockNumbered: 'Nummerierte Liste',
    blockTasks: 'Aufgabenliste',
    blockQuote: 'Zitat',
    blockCode: 'Codeblock',
    blockTable: 'Tabelle',
    blockRule: 'Trennlinie',
    blockMenu: 'Blocktyp',
    blockConvert: 'Block umwandeln',
    placeholderText: 'Text oder „/“ für Befehle',
    blockMenuEmpty: 'Nichts gefunden.',
    blockHandle: 'Zeile verschieben',
    editorPlaceholder: 'Schreiben, oder „/" für einen Block.',
    tableRowAfter: 'Zeile darunter',
    tableRowBefore: 'Zeile darüber',
    tableColAfter: 'Spalte rechts',
    tableColBefore: 'Spalte links',
    tableRowDelete: 'Zeile löschen',
    tableColDelete: 'Spalte löschen',
    tableRemove: 'Tabelle löschen',
    tableResetWidths: 'Breiten zurücksetzen',
    tableAlignLeft: 'Linksbündig',
    tableAlignCenter: 'Zentriert',
    tableAlignRight: 'Rechtsbündig',
    tableRowHandle: 'Zeile auswählen oder verschieben',
    tableColHandle: 'Spalte auswählen oder verschieben',
    tableToolbar: 'Tabelle',

    chainCheck: 'Dokumentation prüfen',
    chainTitle: 'Prüfung der Dokumentation',
    chainRunning: 'Wird geprüft …',
    chainEmpty: 'Für diesen Kontakt ist noch keine Notiz gesperrt.',
    chainOk: 'Alles in Ordnung.',
    chainOkBody: (count: number) =>
      `${count} gesperrte ${count === 1 ? 'Notiz' : 'Notizen'} geprüft, einschließlich der ` +
      'angehängten Dateien. Keine Abweichung.',
    chainBroken: 'Es gibt Abweichungen.',
    chainBrokenBody:
      'Bitte sichern Sie den aktuellen Stand und klären Sie die Ursache, bevor Sie ' +
      'weiterarbeiten.',
    chainContentBroken:
      'Der Inhalt weicht von der gespeicherten Prüfsumme ab — die Zeile wurde nach dem ' +
      'Sperren verändert.',
    chainLinkBroken:
      'Die Kette ist an dieser Stelle unterbrochen — davor wurde eine Notiz entfernt oder ' +
      'eingefügt.',
    chainFileMismatch: 'Der Inhalt dieser Datei weicht ab.',
    chainFileMissing: 'Diese Datei liegt nicht mehr auf der Festplatte.',
    chainEntryOk: 'unverändert',
  },
  invoice: {
    title: 'Rechnungen',
    /** The title of the dialog that asks which contact — not a button label.
     *  The button that starts one says `createAction`; keeping them apart is
     *  the lesson of the one-string-two-purposes bugs in K4 and K5. */
    create: 'Neue Rechnung',
    createAction: 'Rechnung erstellen',
    /** The picker in the create row asks for the **contact**, not for the
     *  Rechnungsempfänger the editor asks for further down (L8): whose
     *  treatment is billed, and who the document goes to, are two questions.
     *  The design writes the same word over both, which is the
     *  one-string-two-purposes bug K4 and K5 each fixed once. */
    forContact: 'Kontakt',
    createHint: 'Für welchen Kontakt? Die Positionen kommen danach im Entwurf dazu.',
    createConfirm: 'Entwurf anlegen',
    /** While the draft of a contact whose name is already known is being
     *  written — the row exists for a moment before it is a row. */
    creating: 'Entwurf wird angelegt …',
    empty: 'Noch keine Rechnungen.',
    emptyDraft: 'Diese Rechnung hat noch keine Position.',

    statusLabel: 'Status',
    statuses: {
      draft: 'Entwurf',
      finalized: 'Festgeschrieben',
      cancelled: 'Storniert',
    },
    types: {
      invoice: 'Rechnung',
      cancellation_invoice: 'Stornorechnung',
    },
    all: 'Alle',
    /** The one chip band of the merged list (D7) — `invoiceListFilters` in
     *  `packages/shared` decides what each of them matches. */
    /** The chip band names the *state* a document is in, not the set of them:
     *  "Entwurf", not "Entwürfe" (K8). */
    filters: {
      draft: 'Entwurf',
      open: 'Offen',
      overdue: 'Überfällig',
      paid: 'Bezahlt',
      cancelled: 'Storniert',
    },
    emptyFiltered: 'Keine Rechnung passt zu diesem Filter.',
    /** In the invoice's own rail, where the day it was settled stands under
     *  the status. The row in the list says it differently — see `paidOnDay`
     *  below, which is a cell and not a sentence. */
    paidOn: (date: string) => `bezahlt ${date}`,
    /** In the status cell, beside the badge. */
    partPaid: (amount: string) => `${amount} bezahlt`,
    paidOnDay: (date: string) => `bezahlt am ${date}`,
    settledOnDay: (date: string) => `am ${date}`,
    /** Beside the due date, in red, once it has passed. */
    overdueSinceDays: (days: number) => (days === 1 ? 'seit 1 Tag' : `seit ${days} Tagen`),

    number: 'Nummer',
    /** The column header, and it says "Empfänger" because that is what the
     *  cell holds since L8: the snapshot's name, else the chosen recipient,
     *  else the contact. It read `contactName` until B3, which was the
     *  contact under a heading promising the recipient. */
    contact: 'Empfänger',
    invoiceDate: 'Rechnungsdatum',
    dueDate: 'Zahlbar bis',
    paidAmount: 'Bezahlt',
    openAmount: 'Offen',
    paymentTermDays: 'Zahlungsziel in Tagen',
    total: 'Betrag',
    introText: 'Einleitungstext',
    outroText: 'Schlusstext',
    diagnosis: 'Diagnose',
    diagnosisHint:
      'Aus den Stammdaten vorbelegt, für diese Rechnung überschreibbar. Erscheint auf der ' +
      'Rechnung.',
    insertTemplate: 'Baustein einfügen',
    textHint:
      'Der eingefügte Text gehört ab dann zu dieser Rechnung und ändert sich nicht mehr mit ' +
      'dem Baustein.',

    lines: 'Positionen',
    lineDescription: 'Bezeichnung',
    lineFeeCode: 'Ziffer',
    lineDate: 'Datum',
    lineQuantity: 'Menge',
    lineUnitPrice: 'Einzelpreis',
    /** One list instead of two (L8): what is on the invoice and what is still
     *  open stand together, and the checkbox is the answer. */
    /** Beside the heading of an unsaved draft, in the idiom L6b settled on. */
    draftPending: 'Entwurf, noch nicht festgeschrieben',
    /** What the design writes as "Nr. 2026-0043 nach dem Festschreiben" — the
     *  same information without the figure one would go on to believe. Two
     *  drafts make such a figure wrong, because whichever is finalized first
     *  takes the value. */
    numberOnFinalize: 'Die Nummer wird beim Festschreiben vergeben.',
    recipient: 'Rechnungsempfänger',
    totalLong: 'Rechnungsbetrag',
    /** Under a position in read mode — where it came from, and a way there. */
    fromActivity: (date: string) => `Vorgang ${date}`,
    /** Said where the field would otherwise look broken: one name and no
     *  dropdown. Names where a second option comes from — the relation, not a
     *  free choice of contact. */
    recipientHint:
      'Ein anderer Empfänger lässt sich in den Stammdaten unter „Verknüpfte Kontakte" ' +
      'als Rechnungsempfänger hinterlegen.',
    paymentTermDaysValue: (days: number) => `${days} ${days === 1 ? 'Tag' : 'Tage'}`,
    saveDraft: 'Als Entwurf speichern',
    linesHint:
      'Positionen aus Vorgängen werden an- und abgehakt; abgehakt sind sie wieder abrechenbar. ' +
      'Freie Positionen gibt es nur auf dieser Rechnung und werden mit dem × entfernt.',
    linesNothingOpen: 'Für diesen Kontakt ist nichts mehr abrechenbar.',
    lineToggle: 'Auf diese Rechnung',
    lineGroupToggle: 'Alle Positionen dieses Vorgangs',
    lineRemove: 'Position entfernen',
    lineMoveUp: 'Nach oben',
    lineMoveDown: 'Nach unten',
    addFreeLine: 'Freie Position',

    billable: 'Offene Positionen',
    billableEmpty: 'Für diesen Kontakt ist nichts offen.',
    billableHint:
      'Positionen aus Vorgängen, die auf keiner aktiven Rechnung stehen. ' +
      'Eine stornierte Rechnung gibt ihre Positionen wieder frei.',
    addSelected: 'Ausgewählte übernehmen',

    save: 'Speichern',
    saving: 'Wird gespeichert …',
    saved: 'Rechnung gespeichert.',
    saveFailed: 'Die Rechnung konnte nicht gespeichert werden.',
    created: 'Entwurf angelegt.',
    preview: 'Vorschau',
    download: 'Dokument öffnen',
    discard: 'Entwurf verwerfen',
    discardTitle: 'Entwurf verwerfen?',
    discardBody:
      'Der Entwurf wird gelöscht. Er hat noch keine Nummer, es entsteht also keine Lücke. ' +
      'Die Positionen werden wieder frei.',
    discarded: 'Entwurf verworfen.',

    finalize: 'Festschreiben',
    finalizeTitle: 'Rechnung festschreiben?',
    finalizeBody:
      'Die Rechnung erhält ihre Nummer, das PDF wird erzeugt und abgelegt. Danach lässt sich ' +
      'nichts mehr ändern — weder Betrag noch Text noch Empfänger. Eine Korrektur ist nur ' +
      'noch über eine Stornorechnung möglich.',
    finalizeConfirm: 'Endgültig festschreiben',
    finalized: 'Rechnung festgeschrieben.',
    finalizeFailed: 'Die Rechnung konnte nicht festgeschrieben werden.',

    paymentState: 'Zahlungsstand',
    cancel: 'Stornieren',
    cancelTitle: 'Rechnung stornieren?',
    cancelBody:
      'Es entsteht eine Stornorechnung mit eigener Nummer und negativen Beträgen. Die ' +
      'ursprüngliche Rechnung bleibt unverändert erhalten, und ihre Positionen werden wieder ' +
      'abrechenbar. Rückgängig machen lässt sich das nicht.',
    cancelConfirm: 'Stornorechnung ausstellen',
    cancelled: 'Stornorechnung ausgestellt.',
    cancelFailed: 'Die Rechnung konnte nicht storniert werden.',
    /** Both directions of the link, wherever a document is shown. */
    cancelledBy: 'Storniert durch',
    cancels: 'Storno zu',

    paymentTermTitle: 'Zahlungsziel',
    paymentTermSaved: 'Zahlungsziel gespeichert.',
    paymentTermSaveFailed: 'Das Zahlungsziel konnte nicht gespeichert werden.',
    numberRanges: 'Nummernkreise',
    numberRangeHint:
      'Die Rechnungsnummer wird beim Festschreiben vergeben und danach nie wieder geändert. ' +
      'Zum Jahreswechsel werden das Präfix auf das neue Jahr und der nächste Wert auf 1 ' +
      'gesetzt.',
    numberRangeCodes: {
      invoice: 'Rechnungen',
    },
    numberRangeNotCreated: 'Noch nicht angelegt',
    numberRangeCreate: 'Nummernkreis anlegen',
    numberRangeCreated: 'Nummernkreis angelegt.',
    prefix: 'Präfix',
    padding: 'Stellen',
    nextValue: 'Nächste Nummer',
    nextNumberPreview: 'Nächste Nummer wäre',
    /** Column headings of the number-range table (K4). The preview column is
     *  headed by the short form; the long one still names the value in prose
     *  where a single range is described. */
    rangeColumnCode: 'Kreis',
    rangeColumnPreview: 'Vorschau',
    numberRangeSaved: 'Nummernkreis gespeichert.',
    numberRangeMissing:
      'Für Rechnungen ist noch kein Nummernkreis eingerichtet. Ohne ihn lässt sich keine ' +
      'Rechnung festschreiben.',

    templates: 'Textbausteine',
    templatesHint: 'Einleitung und Schluss für neue Rechnungen',
    templateKind: 'Art',
    templateKinds: { intro: 'Einleitung', outro: 'Schluss' },
    templateName: 'Name',
    templateBody: 'Text',
    templateDefault: 'Standard',
    templateDefaultHint: 'Wird bei einer neuen Rechnung vorbelegt.',
    templatePaidVariant: 'Variante „bereits bezahlt"',
    templatePaidVariantHint:
      'Nur für Schlusstexte. Wird verwendet, sobald die Aktion „Betrag erhalten" dazukommt.',
    templateActive: 'Aktiv',
    templateNew: 'Neuer Baustein',
    templateSaved: 'Baustein gespeichert.',
    templateRemoved: 'Baustein gelöscht.',
    templateRemoveTitle: 'Baustein löschen?',
    templateRemoveBody: 'Der Textbaustein wird endgültig gelöscht.',
    templateEmpty: 'Noch keine Textbausteine.',

    letterhead: 'Rechnungsvorlage',
    /** Spelled out per case rather than compressed into one sentence with a
     *  semicolon in the middle (B1, J1). The old wording — "Eine Seite trägt
     *  jede Seite der Rechnung; bei zwei Seiten trägt Seite 1 die erste und
     *  Seite 2 alle weiteren" — used "Seite" for the template and for the
     *  invoice in the same breath, and the reader had to work out which was
     *  meant four times over. */
    letterheadHint:
      'Ein PDF mit dem Briefkopf der Praxis. Hat die Vorlage eine Seite, wird sie hinter jede ' +
      'Seite der Rechnung gelegt. Hat sie zwei, kommt die erste hinter die erste ' +
      'Rechnungsseite und die zweite hinter alle weiteren. Der Rechnungsinhalt wird ' +
      'darübergedruckt.',
    letterheadUpload: 'Vorlage hochladen',
    letterheadReplace: 'Vorlage ersetzen',
    letterheadShow: 'Hinterlegte Vorlage ansehen',
    letterheadUploaded: 'Vorlage gespeichert.',
    letterheadNone: 'Keine Vorlage hinterlegt — Rechnungen drucken auf weißem Grund.',
    /** One sentence instead of a chip plus a sentence saying the same thing
     *  twice (B1, J2). What the practitioner needs from this line is which of
     *  the two cases above applies to their file. */
    letterheadOnePageStored: 'Einseitiges PDF hinterlegt.',
    letterheadTwoPagesStored: 'Zweiseitiges PDF hinterlegt.',
    letterheadRemove: 'Vorlage entfernen',
    letterheadRemoveTitle: 'Vorlage entfernen?',
    letterheadRemoveBody:
      'Neue Rechnungen drucken danach auf weißem Grund. Bereits festgeschriebene Rechnungen ' +
      'bleiben unverändert — ihr PDF liegt fertig auf der Platte und wird nie neu erzeugt.',
    letterheadRemoved: 'Vorlage entfernt.',
  },
  payment: {
    title: 'Zahlungen',
    empty: 'Noch keine Zahlung erfasst.',
    add: 'Zahlung erfassen',
    addTitle: 'Zahlung erfassen',
    paidOn: 'Datum',
    amount: 'Betrag',
    amountHint: 'In Euro. Ein negativer Betrag erfasst eine Rückzahlung.',
    method: 'Zahlweg',
    methods: {
      bank_transfer: 'Überweisung',
      card: 'Karte',
      other: 'Sonstiges',
    },
    note: 'Notiz',
    save: 'Erfassen',
    saved: 'Zahlung erfasst.',
    saveFailed: 'Die Zahlung konnte nicht erfasst werden.',
    remove: 'Zahlung löschen',
    removed: 'Zahlung gelöscht.',
    removeTitle: 'Zahlung löschen?',
    /** Names the amount and the date: with several payments on one invoice it
     *  is otherwise unclear which one is about to go. */
    removeBody: (amount: string, date: string) =>
      `Die Zahlung über ${amount} vom ${date} wird gelöscht. Der Zahlungsstand der Rechnung ` +
      'ändert sich entsprechend.',

    sumPaid: 'Bezahlt',
    sumOpen: 'Offen',
    statuses: {
      open: 'Offen',
      partially_paid: 'Teilweise bezahlt',
      paid: 'Bezahlt',
      overpaid: 'Überzahlt',
      cancelled: 'Storniert',
      cancellation: 'Stornorechnung',
    },
    overdueBy: (days: number) => (days === 1 ? 'seit 1 Tag fällig' : `seit ${days} Tagen fällig`),

    settle: 'Betrag erhalten',
    settleTitle: 'Rechnung festschreiben und als bezahlt erfassen?',
    settleBody:
      'Die Rechnung wird festgeschrieben, erhält ihre Nummer und ihr PDF — und es wird sofort ' +
      'eine Zahlung über den vollen Betrag mit Zahlweg „Karte" und dem Rechnungsdatum erfasst. ' +
      'Ist ein Schlusstext für bezahlte Rechnungen hinterlegt, wird er verwendet. Die Zahlung ' +
      'lässt sich danach korrigieren oder löschen, die Rechnung nicht mehr.',
    settleConfirm: 'Festschreiben und bezahlt',
    settled: 'Festgeschrieben und als bezahlt erfasst.',
    /** The one thing that would otherwise go unnoticed for months: a document
     *  that asks for payment although it was settled on the spot. */
    settledWithoutTemplate:
      'Festgeschrieben und als bezahlt erfasst. Es ist kein Schlusstext für bezahlte Rechnungen ' +
      'hinterlegt — das Dokument fordert daher weiterhin zur Zahlung auf. Sie hinterlegen ihn ' +
      'in den Einstellungen unter „Rechnungsstellung".',
  },
  /** The Zahlungen screen (D7): the two tabs that replaced Abrechenbar,
   *  Rechnungen and Bezahlübersicht. */
  payments: {
    title: 'Zahlungen',
    description: 'Was erbracht und noch nicht abgerechnet ist, und was daraus geworden ist.',
    /** The design puts a different sentence here — this one is the app's own
     *  and older; the prototype's H1 line reads "Vom erbrachten Vorgang bis
     *  zum Geldeingang." */
    tabBillable: 'Offene Vorgänge',
    tabInvoices: 'Rechnungen',

    /* The two tiles that are the tab switch (K8). Each says what it is, what
       it is worth, and what makes up that worth. */
    tileBillableValue: 'abrechenbar',
    tileInvoicesValue: 'offen',
    tileBillableParts: (activities: number, items: number) =>
      `${activities} ${activities === 1 ? 'Vorgang' : 'Vorgänge'} · ${items} ${
        items === 1 ? 'Position' : 'Positionen'
      }`,
    tileInvoiceParts: (drafts: number, open: number) =>
      `${drafts} ${drafts === 1 ? 'Entwurf' : 'Entwürfe'} · ${open} offen`,
    tileOverdue: (count: number) =>
      count === 1 ? '1 Rechnung überfällig' : `${count} Rechnungen überfällig`,

    /** The sticky footer of the first tab. */
    selection: (count: number) =>
      count === 1 ? '1 Position ausgewählt' : `${count} Positionen ausgewählt`,
    selectionEmpty: 'Nichts ausgewählt',
    /** Above the list, where the prototype explains what this tab is before
     *  anything is picked, and reports the selection once something is. */
    billableHint:
      'Vergangene Vorgänge, deren Positionen auf keiner Rechnung stehen. ' +
      'Abgerechnet wird je Kontakt.',
    billableSelection: (items: number, amount: string, contacts: number) =>
      `${items} ${items === 1 ? 'Position' : 'Positionen'} gewählt · ${amount} · bei ${contacts} ${
        contacts === 1 ? 'Kontakt' : 'Kontakten'
      }`,
    /** In a group header, once something in it is ticked. */
    groupSelection: (items: number, amount: string) =>
      `${items} ${items === 1 ? 'Position' : 'Positionen'} · ${amount}`,
    groupActivities: (count: number) => `${count} ${count === 1 ? 'Vorgang' : 'Vorgänge'}`,
  },
  /** Everything the date and time fields say. The *format* they follow is not
   *  here — that is `dateFormat` in packages/shared, one descriptor for the
   *  whole application. These are its labels. */
  date: {
    open: 'Kalender öffnen',
    today: 'Heute',
    month: 'Monat',
    year: 'Jahr',
    previousMonth: 'Voriger Monat',
    nextMonth: 'Nächster Monat',
    months: [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ],
    weekdays: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    /** Written out, for the month view's column headings. Not derived from
     *  `Intl` at render time: these are seven fixed words, and a list beside
     *  the short one is easier to read than a formatter call per column. */
    weekdaysLong: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'],
    invalid: (example: string) => `Kein gültiges Datum. Zum Beispiel ${example}.`,
    timeInvalid: (example: string) => `Keine gültige Uhrzeit. Zum Beispiel ${example}.`,
  },
  /**
   * The billable view and the two ways into it. The status of an activity is
   * shown here and never filters — see `billableQuerySchema`.
   */
  billable: {
    description:
      'Alle erbrachten Leistungen, die auf keiner aktiven Rechnung stehen — nach Kontakt ' +
      'gruppiert. Der Status eines Vorgangs wird angezeigt, filtert aber nicht: ein ' +
      'vergangener Vorgang, der noch auf „geplant“ steht, soll auffallen.',
    empty: 'Nichts offen — alles Erbrachte steht auf einer Rechnung.',
    collect: 'Rechnungen erstellen',
    /** In an activity row of the Zahlungen list, where the contact's name is
     *  already in the group header above. */
    openContact: 'Zur Akte',

    draftExists: 'Entwurf vorhanden',
    total: 'Summe:',

    collectTitle: 'Rechnungsentwürfe erzeugen?',
    collectBody:
      'Pro Kontakt entsteht ein Entwurf. Hat ein Kontakt bereits einen, werden die Positionen ' +
      'dort angehängt statt einen zweiten anzulegen.',
    collectConfirm: 'Übernehmen',
    willCreate: (count: number) =>
      count === 1 ? 'neuer Entwurf, 1 Position' : `neuer Entwurf, ${count} Positionen`,
    willAppend: (count: number) =>
      count === 1
        ? 'an vorhandenen Entwurf, 1 Position'
        : `an vorhandenen Entwurf, ${count} Positionen`,
    collected: (count: number) =>
      count === 1 ? 'Entwurf steht bereit.' : `${count} Entwürfe stehen bereit.`,

    /** On a single activity. */
    fromActivity: 'Rechnung erstellen',
    /** On a row and on a chip alike, and deliberately the same word in both
     *  places (L7). It said "Offen" on the row and "Nicht abgerechnet" on the
     *  chip beside it, which is one state under two names. */
    stateOpen: 'Nicht abgerechnet',
    stateBilled: 'Abgerechnet',
  },
  actions: {
    back: 'Zurück',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    close: 'Schließen',
  },
  /** Shared by every catalogue list — `catalogue-controls.tsx` — rather than
   *  each entity repeating "Aktiv"/"Nach oben" under its own key. */
  /** The count row above a list (K3). Its chips only count — they are not
   *  filters, so a zero is left out rather than shown as an empty category. */
  counts: {
    notes: (n: number) => `${n} ${n === 1 ? 'Notiz' : 'Notizen'}`,
    notesAddendum: 'Nachtrag',
    activities: (total: number, upcoming: number) =>
      `${total} ${total === 1 ? 'Vorgang' : 'Vorgänge'} · ${upcoming} kommend`,
    activitiesBilled: 'Abgerechnet',
    activitiesUnbilled: 'Nicht abgerechnet',
  },
  catalogue: {
    active: 'Aktiv',
    /** The column heading. "Aktiv" is what a row *says*; the column is about
     *  the status, which is what the design heads it with (K5). */
    statusColumn: 'Status',
    inactive: 'Inaktiv',
    moveUp: 'Nach oben',
    moveDown: 'Nach unten',
    columns: 'Spalten',
    dragToOrder: 'Zum Sortieren ziehen.',
    moveColumn: (label: string) => `${label} verschieben`,
    visibleColumns: 'Sichtbare Spalten',
  },
  status: {
    loading: 'Wird geladen …',
  },
  placeholder: {
    /** For an area that is deliberately empty, not a stub waiting to be
     *  filled — purely descriptive of now, no promise of what comes next
     *  (`.claude/rules/web.md`, "a form never claims a state that does not exist"). Used
     *  by Übersicht and Zahlungen today. */
    empty: 'Hier gibt es aktuell nichts zu sehen.',
  },
  error: {
    generic: 'Es ist ein unerwarteter Fehler aufgetreten.',
    /** Deliberately different wording from `generic`: the two were identical
     *  once, and an unreachable server read as a server error. */
    serverUnreachable:
      'Der Server ist nicht erreichbar. Bitte prüfen Sie, ob die Anwendung läuft, ' +
      'und versuchen Sie es erneut.',
    notFound: 'Diese Seite gibt es nicht.',
  },
} as const
